import {
  ActionIcon, Alert, Anchor, Badge, Button, Checkbox, Container, Fieldset, Grid, Group,
  LoadingOverlay, NumberInput, Paper, SegmentedControl, Select, Stack, Table, Tabs, Text, Textarea,
  TextInput, Tooltip,
} from '@mantine/core';
import { COMMS_LABEL, COMMS_TYPES } from '../utils/commsPreferences';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAddressBook, IconAlertCircle, IconArrowBackUp, IconBriefcase, IconBuilding, IconBuildingBank, IconCake, IconCash,
  IconCopy, IconEdit, IconFileImport, IconFiles, IconHeart, IconMail, IconMapPin, IconPlus, IconRotate,
  IconShieldCheck, IconTrash, IconUser, IconUsers, IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
import { changedFieldLabels, fieldSuffix, labelizeField } from '../utils/toastFields';
import { useAlertModal } from '../components/AlertModalProvider';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { StatusExplained } from '../components/StatusExplained';
import { diffUpdatePayload, toInt } from '../utils/diffPayload';
import { NOT_ANSWERED, PEP_LINKS, PEP_OPTIONS, US_PERSON_LINKS, US_PERSON_OPTIONS } from '../utils/pepLabels';
import { LabelWithLinks } from '../components/ReferenceLinks';
import { overlapConfirmProps, pendingFieldOverlap } from '../utils/pendingOverlap';
import { StickyDetailHeader } from '../components/StickyHeader';
import { SummaryItem } from '../components/SummaryBox';
import { ApiError } from '../api/client';
import { applyApiErrorToForm, isFieldLevelApiError } from '../utils/applyApiError';
import { AccountRef, AgreementRef, InsurancePolicyRef } from '../components/EntityRef';
import type { AccountPartyCreateInput, AgreementPartyCreateInput, InsurancePolicyPartyInput, PersonInput, PersonKind } from '../api/types';
import { PARTY_ROLE_LABELS } from '../api/types';
import { useAccountInsuranceList } from '../hooks/useAccountInsurance';
import { useAccountParties, useCreateAccountParty, useDeleteAccountParty } from '../hooks/useAccountParties';
import { useAgreementParties, useCreateAgreementParty, useDeleteAgreementParty } from '../hooks/useAgreementParties';
import { useInsurancePolicyPartiesList, useCreateInsurancePolicyParty, useDeleteInsurancePolicyParty } from '../hooks/useInsurancePolicyParties';
import { usePendingOps } from '../hooks/usePendingOps';
import { usePendingCreates } from '../hooks/usePendingCreates';
import { documentResultParts, useDocumentStaging } from '../hooks/useDocuments';
import { DocumentsPanel } from '../components/DocumentsPanel';
import { PersonPhoto } from '../components/PersonPhoto';
import {
  ChildrenBox, ExpectedFlowsEditor, FinancialSituationEditor, SourceOfWealthEditor, UboList,
  blockForSave, childrenForSave,
} from '../components/ClientRecordFields';
import { joinName, suggestNameSplit } from '../utils/nameSplit';
import type {
  ChildEntry, ExpectedFlows, FinancialSituation, SourceOfWealth,
} from '../api/types';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { AddPersonAgreementRoleModal } from '../components/AddPersonAgreementRoleModal';
import { AddPersonAccountRoleModal } from '../components/AddPersonAccountRoleModal';
import { AddPersonPolicyRoleModal } from '../components/AddPersonPolicyRoleModal';
import { RowSeverIcon } from '../components/RowSeverIcon';
import {
  useCascadeDeletePerson, useCreatePerson, useDeletePerson, usePerson,
  usePersonReferences, useRestorePerson, useUpdatePerson,
} from '../hooks/usePersons';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import { IncompleteBanner } from '../components/IncompleteBanner';
import { TabMissingCount, useMissingFields } from '../components/MissingFields';
import { CascadeDeleteModal } from '../components/CascadeDeleteModal';
import { ClientRecordImportModal, sameFormValue } from '../components/ClientRecordImportModal';
import { EmailDraftModal } from '../components/EmailDraftModal';
import { canEmailDrafts } from '../api/emailDrafts';
import { useCurrentUser } from '../hooks/useAuth';

/** S13: values staged from a filled client record, kept until Save / Cancel so the user
 *  can switch between the values before and after the upload ("nothing changes silently"). */
interface ImportedValues {
  fileName: string;
  fields: string[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  showing: 'after' | 'before';
}

// Phase 4b-2 (2026-06-16, #11): pending agreement-party ops staged on the
// Person page and committed in handleSubmit's batch (approach A1 — the page
// is one transaction). Mirrors AgreementDetailPage's PendingPartyOp, inverted
// (this person is fixed; the agreement is picked). create carries a tempKey;
// delete carries the existing party_id.
type PendingAgrPartyOp =
  | { kind: 'delete'; party_id: string }
  | { kind: 'create'; tempKey: string; payload: AgreementPartyCreateInput };

const agrPartyOpKey = (op: PendingAgrPartyOp): string =>
  op.kind === 'create' ? op.tempKey : op.party_id;

// Commit 5/6: the same staged-op shape for account- and policy-party links.
type PendingAcctPartyOp =
  | { kind: 'delete'; party_id: string }
  | { kind: 'create'; tempKey: string; payload: AccountPartyCreateInput };
const acctPartyOpKey = (op: PendingAcctPartyOp): string =>
  op.kind === 'create' ? op.tempKey : op.party_id;

type PendingPolPartyOp =
  | { kind: 'delete'; party_id: string }
  | { kind: 'create'; tempKey: string; payload: InsurancePolicyPartyInput };
const polPartyOpKey = (op: PendingPolPartyOp): string =>
  op.kind === 'create' ? op.tempKey : op.party_id;

const KIND_OPTS = [
  { value: 'NATURAL', label: 'Natural person' },
  { value: 'LEGAL', label: 'Legal entity' },
];
const GENDER_OPTS = [
  { value: '', label: '-' },
  { value: 'M', label: 'M' },
  { value: 'F', label: 'F' },
  { value: 'O', label: 'O' },
];
const ID_TYPE_OPTS = [
  { value: '', label: '-' },
  { value: 'CC', label: 'CC' },
  { value: 'Passaporte', label: 'Passaporte' },
  { value: 'Outro', label: 'Outro' },
  // Added 2026-05-06 (migration 0019): foreign residents in PT carry a
  // Certificado de Residência rather than a Cartão de Cidadão.
  { value: 'Certificado de Residência', label: 'Certificado de Residência' },
];
// ── Dropdown option values MUST exactly match the backend enum strings ──
// The backend uses Pydantic v2 strict enum validation: any mismatch
// (including missing/wrong accents) returns 422.
// Cross-references — keep these in sync with backend/app/models/person.py:
//   PEP_OPTIONS      → PEPStatus       (utils/pepLabels.ts; English labels, P4)
//   MARITAL_STATUS_  → MaritalStatus   (lines 50-58)
//   MARITAL_REGIME_  → MaritalRegime   (lines 61-65)
// Beta-bug 2026-05-26 (Miguel C): these lists were accent-stripped, so every
// edit selecting "Nao"/"Uniao de Facto"/"Viuvo"/"Comunhao …"/"Separacao …"
// silently failed with 422. Accents restored below.
const MARITAL_STATUS_OPTS = [
  { value: '', label: '-' },
  { value: 'Solteiro', label: 'Solteiro' },
  { value: 'Casado', label: 'Casado' },
  { value: 'União de Facto', label: 'União de Facto' },
  { value: 'Viúvo', label: 'Viúvo' },
  { value: 'Divorciado', label: 'Divorciado' },
  // 'Outro' added to backend MaritalStatus in migration 0013 (Phase H1.5f);
  // was missing from this option list until 2026-05-26.
  { value: 'Outro', label: 'Outro' },
];
const MARITAL_REGIME_OPTS = [
  { value: '', label: '-' },
  { value: 'Comunhão de Adquiridos', label: 'Comunhão de Adquiridos' },
  { value: 'Comunhão Geral', label: 'Comunhão Geral' },
  { value: 'Separação de Bens', label: 'Separação de Bens' },
  { value: 'Outro', label: 'Outro' },
];
// P16b: a yes/no that can stay unanswered (tax declaration, listed company).
const YES_NO_OPTS = [
  { value: 'No', label: 'No' },
  { value: 'Yes', label: 'Yes' },
];
const PROF_STATUS_OPTS = [
  { value: '', label: '-' },
  { value: 'Estudante', label: 'Estudante' },
  { value: 'Empregado', label: 'Empregado' },
  { value: 'Desempregado', label: 'Desempregado' },
  { value: 'Reformado', label: 'Reformado' },
  { value: 'Outro', label: 'Outro' },
];

interface FormValues {
  kind: PersonKind;
  display_name: string;
  email: string;
  mobile_country_code: string;
  mobile_num: string;
  fixed_phone_country_code: string;
  fixed_phone_num: string;
  pep_status: string;
  // Beta v3 polish A (2026-05-27): switched from boolean (checkbox) to a
  // tri-state string for a Yes/No Select. '' means "not answered yet" and
  // is skipped on save (mirrors pep_status semantics).
  us_person: '' | 'Yes' | 'No';
  // P13 (migration 0101): email preferences — checkboxes, saved with Save (Pattern A).
  comms_monthly_report: boolean;
  comms_newsletter: boolean;
  comms_event_invitations: boolean;
  comms_commercial_offers: boolean;
  kyc_last_date: string;
  kyc_next_date: string;
  gender: string;
  birth_date: string;
  birth_place_country: string;
  birth_place_state: string;
  birth_place_district: string;
  birth_place_city: string;
  birth_place_freguesia: string;
  nationality_1: string;
  nationality_2: string;
  id_type: string;
  id_number: string;
  id_issuing_country: string;
  id_issue_date: string;
  id_expiry_date: string;
  fiscal_country_1: string;
  tin_1: string;
  fiscal_country_2: string;
  tin_2: string;
  fiscal_country_3: string;
  tin_3: string;
  professional_status: string;
  profession: string;
  employer: string;
  fiscal_address: string;
  fiscal_address_postal_code: string;
  fiscal_address_city: string;
  fiscal_address_state: string;
  fiscal_address_country: string;
  mail_address: string;
  mail_address_postal_code: string;
  mail_address_city: string;
  mail_address_state: string;
  mail_address_country: string;
  marital_status: string;
  marital_regime: string;
  spouse_name: string;
  spouse_id_type: string;
  spouse_id_number: string;
  spouse_id_expiry_date: string;
  spouse_fiscal_country: string;
  spouse_tin: string;
  legal_form: string;
  lei: string;
  nipc: string;
  incorporation_country: string;
  incorporation_date: string;
  registered_office_address: string;
  registered_office_postal_code: string;
  registered_office_city: string;
  registered_office_state: string;
  registered_office_country: string;
  regulatory_authority: string;
  // Beta #6b: PCC + RCBE registry documents (legal-only).
  pcc_code: string;
  pcc_expiry_date: string;
  rcbe_code: string;
  rcbe_expiry_date: string;
  // Beta #5 (migration 0023): LEI annual renewal.
  lei_expiry_date: string;
  // Inc 0 (migration 0057): general TIN + fiscal country, legal-only.
  tin: string;
  fiscal_country: string;
  // P16b (migration 0105): client-record fields. Natural:
  given_names: string;
  surnames: string;
  children_count: string;          // '' = not answered
  children: ChildEntry[];          // as many lines as children_count
  financial_situation: FinancialSituation | null;
  source_of_wealth: SourceOfWealth | null;
  expected_flows: ExpectedFlows | null;
  tax_obligations_met: '' | 'Yes' | 'No';
  // …legal:
  corporate_purpose: string;
  cae_code: string;
  listed_company: '' | 'Yes' | 'No';
  listed_market: string;
  high_risk_countries_declaration: string;
  ownership_structure: string;
}

const EMPTY: FormValues = {
  kind: 'NATURAL',
  display_name: '',
  email: '',
  mobile_country_code: '',
  mobile_num: '',
  fixed_phone_country_code: '',
  fixed_phone_num: '',
  pep_status: '',
  us_person: '',
  comms_monthly_report: false,
  comms_newsletter: false,
  comms_event_invitations: false,
  comms_commercial_offers: false,
  kyc_last_date: '',
  kyc_next_date: '',
  gender: '',
  birth_date: '',
  birth_place_country: '',
  birth_place_state: '',
  birth_place_district: '',
  birth_place_city: '',
  birth_place_freguesia: '',
  nationality_1: '',
  nationality_2: '',
  id_type: '',
  id_number: '',
  id_issuing_country: '',
  id_issue_date: '',
  id_expiry_date: '',
  fiscal_country_1: '',
  tin_1: '',
  fiscal_country_2: '',
  tin_2: '',
  fiscal_country_3: '',
  tin_3: '',
  professional_status: '',
  profession: '',
  employer: '',
  fiscal_address: '',
  fiscal_address_postal_code: '',
  fiscal_address_city: '',
  fiscal_address_state: '',
  fiscal_address_country: '',
  mail_address: '',
  mail_address_postal_code: '',
  mail_address_city: '',
  mail_address_state: '',
  mail_address_country: '',
  marital_status: '',
  marital_regime: '',
  spouse_name: '',
  spouse_id_type: '',
  spouse_id_number: '',
  spouse_id_expiry_date: '',
  spouse_fiscal_country: '',
  spouse_tin: '',
  legal_form: '',
  lei: '',
  nipc: '',
  incorporation_country: '',
  incorporation_date: '',
  registered_office_address: '',
  registered_office_postal_code: '',
  registered_office_city: '',
  registered_office_state: '',
  registered_office_country: '',
  regulatory_authority: '',
  pcc_code: '',
  pcc_expiry_date: '',
  rcbe_code: '',
  rcbe_expiry_date: '',
  lei_expiry_date: '',
  tin: '',
  fiscal_country: '',
  given_names: '',
  surnames: '',
  children_count: '',
  children: [],
  financial_situation: null,
  source_of_wealth: null,
  expected_flows: null,
  tax_obligations_met: '',
  corporate_purpose: '',
  cae_code: '',
  listed_company: '',
  listed_market: '',
  high_risk_countries_declaration: '',
  ownership_structure: '',
};

const yesNo = (v: boolean | null | undefined): '' | 'Yes' | 'No' =>
  (v === true ? 'Yes' : v === false ? 'No' : '');

// #22 (2026-06-10): friendly names for the Save toast's changed-field
// suffix. Person has ~50 fields; the generic labelizeField humanizer in
// utils/toastFields.ts covers nearly all of them (acronym-aware: KYC,
// PEP, TIN, LEI, NIPC, PCC, RCBE, ID, US…). Only the underivable ones
// get an override here.
const PERSON_FIELD_LABELS: Record<string, string> = {
  kind: 'Person type',
  // #27 polish (2026-06-11, §3j): the validation alert showed
  // machine-styled "display name". The input is labelled "Full name" /
  // "Registered name" depending on kind; "Name" covers both surfaces
  // (validation alert + save toast).
  display_name: 'Name',
  mobile_num: 'Mobile number',
  fixed_phone_num: 'Fixed phone number',
  // P13: email preferences (one shared list — utils/commsPreferences).
  ...COMMS_LABEL,
  // P16b: client-record fields.
  given_names: 'Given names',
  surnames: 'Surnames',
  children_count: 'Number of children',
  children: 'Children',
  financial_situation: 'Financial situation',
  source_of_wealth: 'Source of wealth',
  expected_flows: 'Expected flows',
  tax_obligations_met: 'Tax obligations met',
  corporate_purpose: 'Corporate purpose',
  cae_code: 'CAE code',
  listed_company: 'Listed company',
  listed_market: 'Listed market',
  high_risk_countries_declaration: 'High-risk countries declaration',
  ownership_structure: 'Ownership structure',
};

function numToStr(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

export function PersonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  // D2-polish 2: support [+ Create new person] inline flow. The caller (e.g.
  // AgreementPartyDetailPage) navigates here with ?return_to=<encoded URL>&
  // return_field=person_id. After save, we redirect to that URL with the
  // new person's id appended as the named query param.
  const returnTo = isNew ? searchParams.get('return_to') : null;
  const returnField = isNew ? searchParams.get('return_field') : null;

  // Task #245 (2026-06-02): include_deleted=true so the detail page can
  // load + render soft-deleted records (DELETED state + Restore button).
  const { data: person, isLoading } = usePerson(isNew ? undefined : id, true);
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson(id ?? '');
  const deletePerson = useDeletePerson();
  const cascadeDeletePerson = useCascadeDeletePerson();
  const restorePerson = useRestorePerson();
  const confirmDelete = useConfirmDelete();
  // Beta #11 cascade-delete (2026-05-26): refs is fetched on-demand
  // (enabled=false) when the user clicks Delete; the resulting report
  // drives the CascadeDeleteModal.
  const refs = usePersonReferences(id, false);
  const [cascadeOpen, setCascadeOpen] = React.useState(false);

  // Phase 4b-2 (2026-06-16, #11): staged agreement-party create/delete on the
  // Roles tab, committed in handleSubmit's batch (approach A1). Account- and
  // policy-party staging follow in commits 5/6 (one party type per commit,
  // C-164). Hooks reused (not the api directly) so cache invalidation rides
  // their onSuccess; r.pending distinguishes filed (RM) from auto-applied.
  const queryClient = useQueryClient();
  const createAgrParty = useCreateAgreementParty();
  const deleteAgrParty = useDeleteAgreementParty();
  const agrPartyQ = usePendingOps<PendingAgrPartyOp>(agrPartyOpKey);
  const [committing, setCommitting] = React.useState(false);

  const stageAgrRoleCreate = React.useCallback((payload: AgreementPartyCreateInput) => {
    agrPartyQ.stage({ kind: 'create', tempKey: crypto.randomUUID(), payload });
  }, [agrPartyQ]);
  const stageAgrRoleDelete = React.useCallback((party_id: string) => {
    agrPartyQ.stage({ kind: 'delete', party_id });
  }, [agrPartyQ]);

  // Commit staged agreement-party ops through maker-checker, in STAGED order
  // (a delete staged before a re-add of the same (person, role) must hit the
  // DB in that order or the alive-row partial-unique index 409s the create).
  // Mirrors AgreementDetailPage.commitPendingPartyOps response handling.
  const commitAgrPartyOps = React.useCallback(
    async (ops: readonly PendingAgrPartyOp[]): Promise<{
      applied: number; filed: number; failedKeys: Set<string>; firstError?: string;
    }> => {
      let applied = 0; let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        const key = agrPartyOpKey(op);
        try {
          const r = op.kind === 'create'
            ? await createAgrParty.mutateAsync(op.payload)
            : await deleteAgrParty.mutateAsync(op.party_id);
          if (r.pending) filed++; else applied++;
        } catch (e) {
          failedKeys.add(key);
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      // Filed creates surface from the pending CRs — refresh that cache so any
      // dependent view updates the moment the Save toast fires.
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      return { applied, filed, failedKeys, firstError };
    },
    [createAgrParty, deleteAgrParty, queryClient],
  );

  // Phase 4b-2 commit 5: account-party staging (same shape as agreement).
  const createAcctParty = useCreateAccountParty();
  const deleteAcctParty = useDeleteAccountParty();
  const acctPartyQ = usePendingOps<PendingAcctPartyOp>(acctPartyOpKey);
  const stageAcctRoleCreate = React.useCallback((payload: AccountPartyCreateInput) => {
    acctPartyQ.stage({ kind: 'create', tempKey: crypto.randomUUID(), payload });
  }, [acctPartyQ]);
  const stageAcctRoleDelete = React.useCallback((party_id: string) => {
    acctPartyQ.stage({ kind: 'delete', party_id });
  }, [acctPartyQ]);
  const commitAcctPartyOps = React.useCallback(
    async (ops: readonly PendingAcctPartyOp[]): Promise<{
      applied: number; filed: number; failedKeys: Set<string>; firstError?: string;
    }> => {
      let applied = 0; let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        const key = acctPartyOpKey(op);
        try {
          const r = op.kind === 'create'
            ? await createAcctParty.mutateAsync(op.payload)
            : await deleteAcctParty.mutateAsync(op.party_id);
          if (r.pending) filed++; else applied++;
        } catch (e) {
          failedKeys.add(key);
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      return { applied, filed, failedKeys, firstError };
    },
    [createAcctParty, deleteAcctParty, queryClient],
  );

  // Phase 4b-2 commit 6: policy-party staging (same shape).
  const createPolParty = useCreateInsurancePolicyParty();
  const deletePolParty = useDeleteInsurancePolicyParty();
  const polPartyQ = usePendingOps<PendingPolPartyOp>(polPartyOpKey);
  const stagePolRoleCreate = React.useCallback((payload: InsurancePolicyPartyInput) => {
    polPartyQ.stage({ kind: 'create', tempKey: crypto.randomUUID(), payload });
  }, [polPartyQ]);
  const stagePolRoleDelete = React.useCallback((party_id: string) => {
    polPartyQ.stage({ kind: 'delete', party_id });
  }, [polPartyQ]);
  const commitPolPartyOps = React.useCallback(
    async (ops: readonly PendingPolPartyOp[]): Promise<{
      applied: number; filed: number; failedKeys: Set<string>; firstError?: string;
    }> => {
      let applied = 0; let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        const key = polPartyOpKey(op);
        try {
          const r = op.kind === 'create'
            ? await createPolParty.mutateAsync(op.payload)
            : await deletePolParty.mutateAsync(op.party_id);
          if (r.pending) filed++; else applied++;
        } catch (e) {
          failedKeys.add(key);
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      return { applied, filed, failedKeys, firstError };
    },
    [createPolParty, deletePolParty, queryClient],
  );

  // P23 (2026-10-10): documents on this person — staged in Edit mode, sent on Save.
  const docs = useDocumentStaging('persons', isNew ? undefined : id);

  // Phase A3.h (2026-06-09): relationship rollups for the header summary
  // box. Queries are conditional on having a saved Person (id) and share
  // cache keys with the Roles tab's own queries (the keys match exactly),
  // so React Query dedupes — no double fetch on page load. Counts the
  // PARTY ROWS, not distinct entities; a Person playing two roles on the
  // same Agreement counts as two for header purposes (matches the Roles
  // tab presentation).
  const headerAgreementParties = useAgreementParties({
    person_id: !isNew ? id : '',
    limit: 200,
  });
  const headerAccountParties = useAccountParties({
    person_id: !isNew ? id : '',
    limit: 200,
  });
  const headerPolicyParties = useInsurancePolicyPartiesList(undefined, undefined, !isNew ? id : undefined,
    { limit: 200 });   // 200, like the other role lists (the default 50 cut it, C-374)

  const initial: FormValues = isNew || !person ? EMPTY : {
    kind: person.kind,
    display_name: person.display_name,
    email: person.email ?? '',
    mobile_country_code: numToStr(person.mobile_country_code),
    mobile_num: person.mobile_num ?? '',
    fixed_phone_country_code: numToStr(person.fixed_phone_country_code),
    fixed_phone_num: person.fixed_phone_num ?? '',
    pep_status: person.pep_status ?? '',
    // Beta v3 polish A: null → '' (unset); true → 'Yes'; false → 'No'.
    us_person: person.us_person === true ? 'Yes'
      : person.us_person === false ? 'No' : '',
    comms_monthly_report: !!person.comms_monthly_report,
    comms_newsletter: !!person.comms_newsletter,
    comms_event_invitations: !!person.comms_event_invitations,
    comms_commercial_offers: !!person.comms_commercial_offers,
    kyc_last_date: person.kyc_last_date ?? '',
    kyc_next_date: person.kyc_next_date ?? '',
    gender: person.gender ?? '',
    birth_date: person.birth_date ?? '',
    birth_place_country: person.birth_place_country ?? '',
    birth_place_state: person.birth_place_state ?? '',
    birth_place_district: person.birth_place_district ?? '',
    birth_place_city: person.birth_place_city ?? '',
    birth_place_freguesia: person.birth_place_freguesia ?? '',
    nationality_1: person.nationality_1 ?? '',
    nationality_2: person.nationality_2 ?? '',
    id_type: person.id_type ?? '',
    id_number: person.id_number ?? '',
    id_issuing_country: person.id_issuing_country ?? '',
    id_issue_date: person.id_issue_date ?? '',
    id_expiry_date: person.id_expiry_date ?? '',
    fiscal_country_1: person.fiscal_country_1 ?? '',
    tin_1: person.tin_1 ?? '',
    fiscal_country_2: person.fiscal_country_2 ?? '',
    tin_2: person.tin_2 ?? '',
    fiscal_country_3: person.fiscal_country_3 ?? '',
    tin_3: person.tin_3 ?? '',
    professional_status: person.professional_status ?? '',
    profession: person.profession ?? '',
    employer: person.employer ?? '',
    fiscal_address: person.fiscal_address ?? '',
    fiscal_address_postal_code: person.fiscal_address_postal_code ?? '',
    fiscal_address_city: person.fiscal_address_city ?? '',
    fiscal_address_state: person.fiscal_address_state ?? '',
    fiscal_address_country: person.fiscal_address_country ?? '',
    mail_address: person.mail_address ?? '',
    mail_address_postal_code: person.mail_address_postal_code ?? '',
    mail_address_city: person.mail_address_city ?? '',
    mail_address_state: person.mail_address_state ?? '',
    mail_address_country: person.mail_address_country ?? '',
    marital_status: person.marital_status ?? '',
    marital_regime: person.marital_regime ?? '',
    spouse_name: person.spouse_name ?? '',
    spouse_id_type: person.spouse_id_type ?? '',
    spouse_id_number: person.spouse_id_number ?? '',
    spouse_id_expiry_date: person.spouse_id_expiry_date ?? '',
    spouse_fiscal_country: person.spouse_fiscal_country ?? '',
    spouse_tin: person.spouse_tin ?? '',
    legal_form: person.legal_form ?? '',
    lei: person.lei ?? '',
    nipc: person.nipc ?? '',
    incorporation_country: person.incorporation_country ?? '',
    incorporation_date: person.incorporation_date ?? '',
    registered_office_address: person.registered_office_address ?? '',
    registered_office_postal_code: person.registered_office_postal_code ?? '',
    registered_office_city: person.registered_office_city ?? '',
    registered_office_state: person.registered_office_state ?? '',
    registered_office_country: person.registered_office_country ?? '',
    regulatory_authority: person.regulatory_authority ?? '',
    pcc_code: person.pcc_code ?? '',
    pcc_expiry_date: person.pcc_expiry_date ?? '',
    rcbe_code: person.rcbe_code ?? '',
    rcbe_expiry_date: person.rcbe_expiry_date ?? '',
    lei_expiry_date: person.lei_expiry_date ?? '',
    tin: person.tin ?? '',
    fiscal_country: person.fiscal_country ?? '',
    given_names: person.given_names ?? '',
    surnames: person.surnames ?? '',
    children_count: numToStr(person.children_count),
    children: person.children ?? [],
    financial_situation: person.financial_situation ?? null,
    source_of_wealth: person.source_of_wealth ?? null,
    expected_flows: person.expected_flows ?? null,
    tax_obligations_met: yesNo(person.tax_obligations_met),
    corporate_purpose: person.corporate_purpose ?? '',
    cae_code: person.cae_code ?? '',
    listed_company: yesNo(person.listed_company),
    listed_market: person.listed_market ?? '',
    high_risk_countries_declaration: person.high_risk_countries_declaration ?? '',
    ownership_structure: person.ownership_structure ?? '',
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      display_name: (v) => (v.trim().length > 0 ? null : 'Required'),
    },
  });
  const pageDirty =
    form.isDirty() || !agrPartyQ.isEmpty || !acctPartyQ.isEmpty || !polPartyQ.isEmpty
    || !docs.queue.isEmpty;
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('person-form', pageDirty, !isNew);
  const confirmDiscard = useDirtyFormGuard(pageDirty, saveForGuard);
  const [editMode, setEditMode] = React.useState(isNew);
  // P34: the Email button (Admin / Compliance / RM; Read mode too).
  const { user: me } = useCurrentUser();
  const [emailOpen, setEmailOpen] = React.useState(false);
  const { showAlert, confirm } = useAlertModal();
  // Pedro 2026-10-10: unfilled required fields (completeness rules) are outlined in
  // violet and counted on their tab; the banner's fields open their tab.
  const missing = useMissingFields('PERSON', isNew ? undefined : person?.id);
  const hl = (f: keyof FormValues) => missing.input(f, form.values[f]);
  const openTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'identity') next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  React.useEffect(() => {
    // #31 (2026-06-11): !editMode gate added for class consistency with
    // the other detail pages — a refetch must never clobber an open edit.
    if (!isNew && person && !editMode) {
      form.setValues(initial);
      form.resetDirty(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id, isNew]);

  // S13 (Pedro 2026-10-11): import from a filled client record. The ticked values are
  // staged in the form (saved only on Save); until then the banner switches between the
  // values before and after the upload. Leaving Edit mode (Save or Cancel) ends it.
  const [importOpen, setImportOpen] = React.useState(false);
  const [imported, setImported] = React.useState<ImportedValues | null>(null);
  React.useEffect(() => { if (!editMode) setImported(null); }, [editMode]);
  const fieldLabel = (f: string) => PERSON_FIELD_LABELS[f] ?? labelizeField(f);

  function applyImported(values: Record<string, unknown>, keepFile: File | null, fileName: string) {
    const fields = Object.keys(values);
    const cur = form.values as unknown as Record<string, unknown>;
    const before = Object.fromEntries(fields.map((f) => [f, cur[f]]));
    if (fields.length) {
      form.setValues((v) => ({ ...v, ...(values as Partial<FormValues>) }));
      setImported({ fileName, fields, before, after: values, showing: 'after' });
    }
    if (keepFile) docs.stageUpload('CLIENT_RECORD_FORM', null, keepFile);
    notifications.show({
      color: 'orange',
      message: `${fields.length} value${fields.length === 1 ? '' : 's'} from ${fileName} staged`
        + `${keepFile ? ' · the file is staged to keep' : ''} — nothing is saved until you press Save`,
    });
  }

  function showImported(which: 'after' | 'before') {
    if (!imported || which === imported.showing) return;
    const shownNow = imported.showing === 'after' ? imported.after : imported.before;
    const target = which === 'after' ? imported.after : imported.before;
    const cur = form.values as unknown as Record<string, unknown>;
    // A field edited by hand since keeps that edit and leaves the switch.
    const fields = imported.fields.filter((f) => sameFormValue(f, cur[f], shownNow[f]));
    form.setValues((v) => ({ ...v, ...(Object.fromEntries(fields.map((f) => [f, target[f]])) as Partial<FormValues>) }));
    setImported({ ...imported, fields, showing: which });
  }

  const isLegal = form.values.kind === 'LEGAL';
  const _err = isNew ? createPerson.error : updatePerson.error;
  const apiError = (_err instanceof ApiError && !isFieldLevelApiError(_err))
    ? _err.detail : null;
  const isDeleted = !!person?.deleted_at;

  // P16b G1: "suggest, then separate".
  function fillDisplayNameFromParts() {
    if (form.values.display_name.trim()) return;
    const full = joinName(form.values.given_names, form.values.surnames);
    if (full) form.setFieldValue('display_name', full);
  }
  function suggestNameParts() {
    const { given, surnames } = suggestNameSplit(form.values.display_name);
    form.setFieldValue('given_names', given);
    form.setFieldValue('surnames', surnames);
  }

  function copyFiscalToMail() {
    // #68 (2026-06-28): kind-aware — for a legal entity the primary address is
    // the registered office (fiscal_address* is CHECK-forbidden for LEGAL).
    form.setValues((v) => ({
      ...v,
      mail_address: isLegal ? v.registered_office_address : v.fiscal_address,
      mail_address_postal_code: isLegal ? v.registered_office_postal_code : v.fiscal_address_postal_code,
      mail_address_city: isLegal ? v.registered_office_city : v.fiscal_address_city,
      // #69: registered office now has its own State, so legal copies it (was
      // keeping the existing mail state before the column existed).
      mail_address_state: isLegal ? v.registered_office_state : v.fiscal_address_state,
      mail_address_country: isLegal ? v.registered_office_country : v.fiscal_address_country,
    }));
    notifications.show({
      message: isLegal ? 'Correspondence address copied from registered office' : 'Mail address copied from fiscal address',
      color: 'blue',
    });
  }

  // Beta v3 polish C (2026-05-27): fields the user can intentionally
  // clear back to "-". Without this, the save loop's "skip empty
  // values" rule meant clearing a previously-set PEP / US-Person back
  // to the empty option silently no-op'd on the server. For these
  // fields, '' on save means "send null".
  // #50 smoke (2026-06-12, Pedro's catch): clearing the EMAIL silently
  // no-op'd the same way — the form showed blank, the server kept the
  // value, and the refetch "resurrected" it (CR payload was {} in the
  // audit log). Contact-class fields added; the broader "which optional
  // Person fields are clearable" sweep is a filed follow-up.
  const NULLABLE_ON_CLEAR = new Set<keyof FormValues>([
    'pep_status', 'us_person',
    'email', 'mobile_country_code', 'mobile_num',
    'fixed_phone_country_code', 'fixed_phone_num',
    // Inc 0b (2026-06-21): the new legal TIN + fiscal country are clearable
    // (required-but-clearable — clearing re-fires the completeness flag).
    // The sibling legal-identity fields (nipc/lei/legal_form/...) are NOT yet
    // clearable — that remains the #36 "which optional fields are clearable"
    // sweep.
    'tin', 'fiscal_country',
    // #71 smoke (2026-06-28, Pedro): address fields are clearable — clearing one
    // back to empty must NULL the column. Without this the "skip empty" rule
    // no-op'd the clear ("Nothing to save", old value persisted). Advances the #36
    // "which optional fields are clearable" sweep for the address block.
    'fiscal_address', 'fiscal_address_postal_code', 'fiscal_address_city', 'fiscal_address_state',
    'fiscal_address_country',
    'mail_address', 'mail_address_postal_code', 'mail_address_city', 'mail_address_state',
    'mail_address_country',
    'registered_office_address', 'registered_office_postal_code', 'registered_office_city',
    'registered_office_state', 'registered_office_country',
    // P16b (migration 0105): every client-record field can be cleared back to
    // "not answered" (the blocks report null when emptied).
    'given_names', 'surnames', 'children_count', 'children',
    'financial_situation', 'source_of_wealth', 'expected_flows', 'tax_obligations_met',
    'corporate_purpose', 'cae_code', 'listed_company', 'listed_market',
    'high_risk_countries_declaration', 'ownership_structure',
  ]);

  // P16b: form shape → API shape for the client-record fields.
  const CLIENT_RECORD_TRANSFORM: Record<string, (v: unknown) => unknown> = {
    children_count: toInt,
    children: (v) => childrenForSave(v as ChildEntry[]),
    tax_obligations_met: (v) => v === 'Yes',
    listed_company: (v) => v === 'Yes',
  };

  async function handleSubmit(rawValues: FormValues) {
    // Smoke 2026-10-11 (S3): the editors keep blank lines while editing (an added line must
    // stay on screen); here a block holding nothing becomes "not answered" (null), so an
    // untouched block never reads as a change.
    const values: FormValues = {
      ...rawValues,
      financial_situation: blockForSave(rawValues.financial_situation),
      source_of_wealth: blockForSave(rawValues.source_of_wealth),
      expected_flows: blockForSave(rawValues.expected_flows),
    };
    const payload: PersonInput = {};
    const rec = payload as Record<string, unknown>;
    for (const [k, v] of Object.entries(values)) {
      // P16b: a new person's client-record fields are sent only when filled in
      // (a blank block / no children is simply "not answered"); converted to the
      // API shape like an update.
      if (k in CLIENT_RECORD_TRANSFORM || k === 'financial_situation'
          || k === 'source_of_wealth' || k === 'expected_flows') {
        if (v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) continue;
        const t = CLIENT_RECORD_TRANSFORM[k];
        const out = t ? t(v) : v;
        if (out !== undefined && out !== null) rec[k] = out;
        continue;
      }
      // Explicit clear: '' / undefined on a NULLABLE_ON_CLEAR field
      // becomes a null patch so the backend resets the column.
      if ((v === '' || v === undefined) && NULLABLE_ON_CLEAR.has(k as keyof FormValues)) {
        rec[k] = null;
        continue;
      }
      if (v === '' || v === undefined) continue;
      if (k === 'us_person') {
        // Beta v3 polish A: 'Yes' → true; 'No' → false. '' is now
        // handled by the NULLABLE_ON_CLEAR branch above.
        rec.us_person = v === 'Yes';
        continue;
      }
      if (k === 'mobile_country_code' || k === 'fixed_phone_country_code') {
        const n = parseInt(v as string, 10);
        if (!Number.isNaN(n)) rec[k] = n;
        continue;
      }
      rec[k] = v;
    }

    if (isNew) {
      createPerson.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty(values);
          // Bug fix (2026-06-09 during A1.f): after create-then-navigate
          // (replace:true), the component re-renders with isNew=false
          // but useState(isNew) doesn't re-initialize, so editMode stays
          // true and the user sees Cancel/Save/Delete instead of Edit.
          // Flip editMode explicitly. Harmless on the returnTo and
          // change-requests branches (component unmounts).
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your new person was filed. It is not yet visible — Compliance must approve it first.',
            });
            navigate(`/change-requests/${r.changeRequestId}`);
          } else {
            notifications.show({ message: 'Person created', color: 'green' });
            // D2-polish 2: if we were invoked via a picker [+ Create new] flow,
            // ferry the new id back to the originating page (return_field set).
            // #6 (2026-09-12): a bare return_to (no return_field) — e.g. the
            // Agreement "Create new person" chooser — just goes back to that
            // page; the caller re-picks the now-existing person there.
            if (returnTo) {
              const suffix = returnField
                ? `${returnTo.includes('?') ? '&' : '?'}${returnField}=${r.entity.id}`
                : '';
              navigate(`${returnTo}${suffix}`, { replace: true });
            } else {
              navigate(`/persons/${r.entity.id}`, { replace: true });
            }
          }
        },
        onError: (err) => {
          applyApiErrorToForm(form, err);
        },
      });
    } else {
      // #22 (2026-06-10): Save toast names the changed fields, against the
      // pre-save baseline. Person has ~50 fields, so labels come from the
      // generic labelizeField humanizer plus a few overrides; the cap
      // inside fieldSuffix (3 + "+N more") keeps bulk edits compact.
      const changedLabels = changedFieldLabels(values, initial, PERSON_FIELD_LABELS);
      // #36 (2026-06-11): UPDATE sends ONLY changed fields — accurate op
      // labels on the pending badge and clean CR diffs. Clear semantics
      // unchanged from before: only the NULLABLE_ON_CLEAR fields
      // (pep_status, us_person) send explicit null when emptied;
      // widening that set is a Pedro-owned decision (see task #36).
      const updatePayload = diffUpdatePayload(
        values as unknown as Record<string, unknown>,
        initial as unknown as Record<string, unknown>,
        {
          nullableOnClear: NULLABLE_ON_CLEAR as ReadonlySet<string>,
          transform: {
            us_person: (v) => v === 'Yes',
            mobile_country_code: toInt,
            fixed_phone_country_code: toInt,
            ...CLIENT_RECORD_TRANSFORM,
          },
        },
      ) as PersonInput;
      // #37/old-#347 (2026-06-11, Pedro's decision: warn, don't block):
      // if a pending CR already changes any field in this payload, ask
      // before filing a competing change.
      const overlap = pendingFieldOverlap(
        updatePayload as Record<string, unknown>,
        person?.pending_change_requests ?? null,
      );
      if (overlap) {
        const ok = await confirm(overlapConfirmProps(
          overlap, (f) => PERSON_FIELD_LABELS[f] ?? labelizeField(f),
        ));
        if (!ok) return;
      }
      // §3g / Phase 4b-2: the page is one transaction — the identity field
      // UPDATE and any staged party ops (agreement / account / policy roles)
      // commit together on Save.
      const agrOps = agrPartyQ.ops;
      const acctOps = acctPartyQ.ops;
      const polOps = polPartyQ.ops;
      const hasPartyOps = agrOps.length > 0 || acctOps.length > 0 || polOps.length > 0;
      const hasDocOps = !docs.queue.isEmpty;
      const hasFieldChanges = Object.keys(updatePayload).length > 0;

      if (!hasFieldChanges && !hasPartyOps && !hasDocOps) {
        form.resetDirty(values);
        setEditMode(false);
        notifications.show({ message: 'No changes to save', color: 'gray' });
        return;
      }

      setCommitting(true);
      // Field UPDATE first (await so a failure aborts before party ops fire).
      let fieldFiled = false;
      if (hasFieldChanges) {
        try {
          const r = await updatePerson.mutateAsync(updatePayload);
          if (r.pending) fieldFiled = true;
        } catch (err) {
          setCommitting(false);
          applyApiErrorToForm(form, err);
          return;
        }
      }

      // Then the staged party ops, one queue per relationship type; settle
      // keeps any failed op staged for retry.
      agrPartyQ.clearFailures();
      acctPartyQ.clearFailures();
      polPartyQ.clearFailures();
      const agrRes = await commitAgrPartyOps(agrOps);
      const acctRes = await commitAcctPartyOps(acctOps);
      const polRes = await commitPolPartyOps(polOps);
      agrPartyQ.settle(agrRes.failedKeys, agrRes.firstError);
      acctPartyQ.settle(acctRes.failedKeys, acctRes.firstError);
      polPartyQ.settle(polRes.failedKeys, polRes.firstError);
      // P23: staged document adds / removes (settled inside commit()).
      const docRes = await docs.commit();
      setCommitting(false);

      const applied = agrRes.applied + acctRes.applied + polRes.applied;
      const filed = agrRes.filed + acctRes.filed + polRes.filed;
      const failedCount = agrRes.failedKeys.size + acctRes.failedKeys.size + polRes.failedKeys.size;
      const anyFailed = failedCount > 0 || docRes.failedKeys.size > 0;
      const firstError = agrRes.firstError ?? acctRes.firstError ?? polRes.firstError;

      form.resetDirty(values);
      // Stay in Edit mode if something failed so the user can fix + re-Save.
      if (!anyFailed) setEditMode(false);

      // §3j: aggregate toast names what happened across all surfaces.
      const segs: string[] = [];
      if (hasFieldChanges) {
        segs.push(fieldFiled ? 'identity edits filed for approval' : `identity saved${fieldSuffix(changedLabels)}`);
      }
      if (applied) segs.push(`${applied} role change${applied > 1 ? 's' : ''} applied`);
      if (filed) segs.push(`${filed} role change${filed > 1 ? 's' : ''} filed for approval`);
      // Document successes; a document failure is reported in the alert below.
      segs.push(...documentResultParts({ ...docRes, failedKeys: new Set() }));
      if (anyFailed) {
        const why: string[] = [];
        if (failedCount > 0) {
          why.push(`${failedCount} role change(s) could not be saved${firstError ? `: ${firstError}` : ''}`);
        }
        if (docRes.failedKeys.size > 0) {
          why.push(`${docRes.failedKeys.size} document change(s) could not be saved${docRes.firstError ? `: ${docRes.firstError}` : ''}`);
        }
        showAlert({
          color: 'orange',
          title: 'Partial save',
          message: `${segs.join(' · ') || 'No other changes saved'}. ${why.join('; ')}. They remain staged — fix the cause or unstage, then Save again.`,
        });
      } else if (fieldFiled || filed || docRes.filed) {
        showAlert({
          color: 'orange',
          title: 'Filed for compliance approval',
          message: `${segs.join(' · ')}. Filed items are not visible until Compliance approves.`,
        });
      } else {
        notifications.show({ message: `Saved · ${segs.join(' · ')}`, color: 'green' });
      }
    }
  }

  // Beta #11 cascade-delete confirm handler (2026-05-26). Translates the
  // modal's payload (table → string[]) into the ref_ids the mutation
  // expects; handles cascade_refs_mismatch by re-fetching and re-prompting.
  const handleCascadeConfirm = (refIds: Record<string, string[]>) => {
    if (!person) return;
    cascadeDeletePerson.mutate(
      { id: person.id, refIds: refIds as Record<string, string[]> },
      {
        onSuccess: () => {
          setCascadeOpen(false);
          navigate('/persons');
        },
        onError: async (err) => {
          if (err instanceof ApiError && err.code === 'cascade_refs_mismatch') {
            await refs.refetch();
            showAlert({
              color: 'orange',
              title: 'Attachments changed since you opened this dialog',
              message: err.detail + ' The list has been refreshed — please review and confirm again.',
            });
          } else {
            setCascadeOpen(false);
            showAlert({
              color: 'red',
              title: 'Cascade delete failed',
              message: err instanceof ApiError ? err.detail
                : 'Unexpected error. Check the server log.',
            });
          }
        },
      },
    );
  };

  return (
    <Container size="xl">
      <CascadeDeleteModal
        opened={cascadeOpen}
        onClose={() => setCascadeOpen(false)}
        entityNoun="Person"
        entityLabel={person?.display_name}
        report={refs.data}
        onConfirm={handleCascadeConfirm}
        loading={cascadeDeletePerson.isPending}
      />
      <Stack>
        {!isNew && person && (
          <EmailDraftModal opened={emailOpen} onClose={() => setEmailOpen(false)}
            recordType="person" recordId={person.id} />
        )}
        <StickyDetailHeader
          editing={editMode}
          title={isNew ? 'New person' : person?.display_name ?? '...'}
          actions={!isNew && person && !isDeleted && canEmailDrafts(me?.role) ? (
            <Tooltip withArrow multiline w={280}
              label="Prepares a draft email to this person, with files attached. It opens in Outlook: check it there and press Send.">
              <Button variant="light" leftSection={<IconMail size={16} />} onClick={() => setEmailOpen(true)}>
                Email
              </Button>
            </Tooltip>
          ) : null}
          backOnClick={() => confirmDiscard(() => {
            // #391 sweep (2026-06-09): clear dirty state BEFORE navigating
            // so the passive useBlocker (DirtyFormProvider, #258) doesn't
            // re-trigger the discard modal on the navigate.
            form.setValues(initial);
            form.resetDirty(initial);
            agrPartyQ.clear();
            acctPartyQ.clear();
            polPartyQ.clear();
            docs.queue.clear();
            setEditMode(false);
            navigate(-1);
          })}
          primary={
            // Task #245 (2026-06-02): hide Edit on deleted records.
            !editMode && !isDeleted ? (
              <Button leftSection={<IconEdit size={16} />} onClick={() => setEditMode(true)}>
                Edit
              </Button>
            ) : !editMode && isDeleted ? null : (
              <Group gap="xs">
                <Button variant="default" leftSection={<IconX size={16} />}
                  onClick={() => {
                    confirmDiscard(() => {
                      form.setValues(initial);
                      form.resetDirty(initial);
                      agrPartyQ.clear();
                      acctPartyQ.clear();
                      polPartyQ.clear();
                      docs.queue.clear();
                      setEditMode(false);
                    });
                  }}>
                  Cancel
                </Button>
                <Button form="person-form" type="submit"
                  loading={createPerson.isPending || updatePerson.isPending || committing}
                  disabled={!form.isDirty() && agrPartyQ.isEmpty && acctPartyQ.isEmpty
                    && polPartyQ.isEmpty && docs.queue.isEmpty && !isNew}>
                  {isNew ? 'Save' : 'Save changes'}
                </Button>
              </Group>
            )
          }
          destructive={
            // #7 (2026-06-16): entity Delete only on the Identity tab —
            // relationship (Roles) tab exposes only per-row sever icons (§3h).
            (searchParams.get('tab') ?? 'identity') !== 'identity' ? null
            : !isNew && person && !isDeleted ? (
              <DisabledTooltipButton variant="light" color="red" leftSection={<IconTrash size={16} />}
                disabledReason={editMode ? null : 'Enter Edit mode to delete'}
                enabledTooltip="Deletes this person record"
                onClick={async () => {
                  // Beta #11 cascade flow (2026-05-26): pre-fetch references.
                  // No active refs → fall through to the existing confirm-then-delete.
                  // Active refs → open the CascadeDeleteModal instead.
                  const result = await refs.refetch();
                  const report = result.data;
                  if (!report || !report.has_active_references) {
                    confirmDelete('person', () => deletePerson.mutate(person.id, {
                      onSuccess: () => navigate('/persons'),
                      onError: (err) => {
                        showAlert({
                          color: 'red',
                          title: 'Delete failed',
                          message: err instanceof ApiError ? err.detail
                            : 'Unexpected error. Check the server log.',
                        });
                      },
                    }));
                  } else {
                    setCascadeOpen(true);
                  }
                }}
                loading={deletePerson.isPending || refs.isFetching}>Delete</DisabledTooltipButton>
            ) : !isNew && person && isDeleted ? (
              <Button variant="light" color="green" leftSection={<IconRotate size={16} />}
                onClick={() => restorePerson.mutate(person.id)}
                loading={restorePerson.isPending}>Restore</Button>
            ) : null
          }
        />

        {/* Phase A3.h (2026-06-09): summary box with Person identity + role
            rollups. Surfaces "where is this Person involved" at a glance
            without opening the Roles tab. Counts are PARTY ROWS (one
            Person may hold multiple roles on the same Agreement → counted
            twice) to match what the Roles tab shows. */}
        {!isNew && person && (
          <Paper withBorder p="md" radius="sm">
            <Group justify="space-between" align="center" wrap="wrap">
              <Group gap="lg" wrap="wrap">
                {/* P24 (2026-10-10): natural persons carry an optional photo,
                    changed in Edit mode and committed on Save. */}
                {person.kind === 'NATURAL' && !isDeleted && (
                  <PersonPhoto personId={person.id} name={person.display_name}
                    editMode={editMode} staging={docs} />
                )}
                <SummaryItem label="Name" value={person.display_name} bold />
                <SummaryItem label="Kind" value={person.kind === 'LEGAL' ? 'Legal entity' : 'Particular'} />
                <SummaryItem
                  label="Agreement roles"
                  value={headerAgreementParties.data?.total ?? '…'}
                  centered
                />
                <SummaryItem
                  label="Account roles"
                  value={headerAccountParties.data?.total ?? '…'}
                  centered
                />
                <SummaryItem
                  label="Policy roles"
                  value={headerPolicyParties.data?.total ?? '…'}
                  centered
                />
              </Group>
              {/* B1/#35 (2026-06-11, old #363): pending-CR state in the
                  header — visible from any tab, next to the status pill. */}
              <Group gap="xs" wrap="nowrap">
                {/* S13: read a filled client record into this person (Edit mode; staged). */}
                {!isDeleted && (
                  <DisabledTooltipButton variant="light" size="xs" leftSection={<IconFileImport size={14} />}
                    disabledReason={editMode ? null : 'Enter Edit mode to import'}
                    enabledTooltip="Read a client record the client filled in on a computer, then choose which values to take"
                    onClick={() => setImportOpen(true)}>
                    Import client record
                  </DisabledTooltipButton>
                )}
                <PendingCRBadge crs={person.pending_change_requests} />
                {/* Smoke 2026-10-11 (S2): the same derived rule as the Persons list (C-321) —
                    Active = a role on an Active or Prospect agreement, account or policy.
                    P47: the wording now comes from utils/statusHelp (same as the list). */}
                <StatusExplained entity="person"
                  status={person.deleted_at ? 'Deleted' : person.has_active_role === false ? 'Inactive' : 'Active'}>
                  <Badge size="lg" variant="filled"
                    color={person.deleted_at ? 'red' : person.has_active_role === false ? 'yellow' : 'green'}
                  >
                    {person.deleted_at ? 'Deleted' : person.has_active_role === false ? 'No current role' : 'Active'}
                  </Badge>
                </StatusExplained>
              </Group>
            </Group>
          </Paper>
        )}

        {apiError && <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>{apiError}</Alert>}

        {!isNew && person && <IncompleteBanner entity_type="PERSON" id={person.id} onOpenTab={openTab} />}

        {editMode && imported && (
          <Alert color="orange" variant="light" icon={<IconFileImport size={16} />}
            title={`Values from ${imported.fileName} — not saved yet`}>
            <Group justify="space-between" align="center" wrap="wrap">
              <Text size="sm">
                {imported.fields.length} field{imported.fields.length === 1 ? '' : 's'} from the file
                ({imported.fields.map(fieldLabel).join(', ')}). Switch to compare; Save keeps what is shown.
              </Text>
              <SegmentedControl size="xs" value={imported.showing}
                onChange={(v) => showImported(v as 'after' | 'before')}
                data={[{ label: 'After upload', value: 'after' }, { label: 'Before upload', value: 'before' }]} />
            </Group>
          </Alert>
        )}
        {!isNew && person && (
          <ClientRecordImportModal opened={importOpen} onClose={() => setImportOpen(false)}
            personId={person.id} current={form.values as unknown as Record<string, unknown>}
            label={fieldLabel} onUse={applyImported} />
        )}

        {/* #27 (2026-06-11, C-121 sweep): the only validated field
            (display_name) lives on Identity; a failure while the user is
            on another tab made Save appear dead. Second onSubmit argument
            names the field and jumps back to Identity (= no tab param,
            matching the URL-driven Tabs below). Reference:
            AgreementDetailPage. */}
        {/* noValidate (#27 round 2): see AccountDetailPage — native
            required-validation intercepts the submit before React;
            Mantine validators own the job instead. */}
        <form id="person-form" noValidate onSubmit={form.onSubmit(handleSubmit, (errors) => {
          const fields = Object.keys(errors);
          const labels = fields.map((f) => PERSON_FIELD_LABELS[f] ?? f.replace(/_/g, ' '));
          const next = new URLSearchParams(searchParams);
          next.delete('tab'); // identity is the default tab
          setSearchParams(next, { replace: true });
          showAlert({
            color: 'red',
            title: 'Cannot save — fix the highlighted field(s) first',
            message: `${labels.join(', ')}: ${fields.map((f) => String(errors[f])).join('; ')}`,
          });
        })}>
          <Stack pos="relative">
            <LoadingOverlay visible={!isNew && isLoading} />
              {/* Beta v2 polish F (2026-05-26): tabs are URL-driven so
                  Compliance deep-links from /compliance/expiring (?tab=legal)
                  land on the right tab. Falls back to "identity" when no
                  tab query param is set. */}
              <Tabs
                value={searchParams.get('tab') ?? 'identity'}
                onChange={(v) => { if (v) openTab(v); }}
                keepMounted
              >
                <Tabs.List>
                  {/* Beta #13 + #14 (2026-05-26): the former Identification tab
                      has been folded into Identity, and PEP + US Person have
                      been moved here from Contact. Identity now answers
                      "who is this person?" in one place. */}
                  {/* DF4 (Pedro 2026-10-12): the tabs follow the client record's order —
                      who the person is, personal details (birth, contact, addresses),
                      family, tax residence, occupation — then the KYC / financial blocks.
                      A company's Corporate tab comes straight after Identity, as on its
                      form. Only the order changed; every field stays on its tab. */}
                  <Tabs.Tab value="identity" leftSection={<IconUser size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.identity} />}>Identity</Tabs.Tab>
                  {isLegal && <Tabs.Tab value="legal" leftSection={<IconBuilding size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.legal} />}>Corporate</Tabs.Tab>}
                  {!isLegal && <Tabs.Tab value="birth" leftSection={<IconCake size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.birth} />}>Birth</Tabs.Tab>}
                  <Tabs.Tab value="contact" leftSection={<IconAddressBook size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.contact} />}>Contact</Tabs.Tab>
                  <Tabs.Tab value="addresses" leftSection={<IconMapPin size={14} />}>Addresses</Tabs.Tab>
                  {/* P16b (Pedro 2026-10-10): "Marital & spouse" renamed "Family" (spouse +
                      children). The tab key stays "marital" so existing links still work. */}
                  {!isLegal && <Tabs.Tab value="marital" leftSection={<IconHeart size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.marital} />}>Family</Tabs.Tab>}
                  {!isLegal && <Tabs.Tab value="fiscal" leftSection={<IconCash size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.fiscal} />}>Fiscal</Tabs.Tab>}
                  {!isLegal && <Tabs.Tab value="profession" leftSection={<IconBriefcase size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.profession} />}>Profession</Tabs.Tab>}
                  <Tabs.Tab value="kyc" leftSection={<IconShieldCheck size={14} />}
                    rightSection={<TabMissingCount count={missing.tabCounts.kyc} />}>KYC</Tabs.Tab>
                  {!isNew && person && <Tabs.Tab value="roles" leftSection={<IconUsers size={14} />}>Roles</Tabs.Tab>}
                  {!isNew && person && <Tabs.Tab value="documents" leftSection={<IconFiles size={14} />}>Documents</Tabs.Tab>}
                </Tabs.List>

                <Tabs.Panel value="identity" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <Fieldset>
                    <Grid>
                      <Grid.Col span={4}>
                        <Select label="Kind" data={KIND_OPTS} {...form.getInputProps('kind')}
                          disabled={!isNew} allowDeselect={false} />
                      </Grid.Col>
                      <Grid.Col span={8}>
                        <TextInput label={isLegal ? 'Registered name' : 'Full name'} required
                          {...form.getInputProps('display_name')} {...hl('display_name')} />
                      </Grid.Col>
                      {/* P16b G1 (Pedro 2026-10-10, "suggest, then separate"): given names
                          and surnames as the client record asks them. An empty full name is
                          filled from them; "Suggest" splits the full name by the agreed rule
                          for the user to check. After that each is edited on its own. */}
                      {!isLegal && (
                        <>
                          <Grid.Col span={5}>
                            <TextInput label="Given names" {...form.getInputProps('given_names')}
                              {...hl('given_names')} onBlur={fillDisplayNameFromParts} />
                          </Grid.Col>
                          <Grid.Col span={5}>
                            <TextInput label="Surnames" {...form.getInputProps('surnames')}
                              {...hl('surnames')} onBlur={fillDisplayNameFromParts} />
                          </Grid.Col>
                          <Grid.Col span={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Tooltip withArrow multiline w={260}
                              label="Fill Given names and Surnames from the full name (first two words when the name has four or more, otherwise the first; da / de / do / dos / das / e go with the surname). Check before saving.">
                              <Button variant="light" fullWidth
                                disabled={!form.values.display_name.trim()} onClick={suggestNameParts}>
                                Suggest
                              </Button>
                            </Tooltip>
                          </Grid.Col>
                        </>
                      )}
                      {/* Beta #13 (2026-05-26): identity-document fields, only
                          for natural persons. Legal entities use NIPC/LEI
                          on the Legal entity tab. */}
                      {!isLegal && (
                        <>
                          <Grid.Col span={3}>
                            <Select label="ID type" data={ID_TYPE_OPTS} {...form.getInputProps('id_type')} {...hl('id_type')} />
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <TextInput label="ID number" {...form.getInputProps('id_number')} {...hl('id_number')} />
                          </Grid.Col>
                          <Grid.Col span={5}>
                            <TextInput label="Issuing country" {...form.getInputProps('id_issuing_country')} {...hl('id_issuing_country')} />
                          </Grid.Col>
                          <Grid.Col span={3}>
                            <TextInput label="Issue date" type="date" {...form.getInputProps('id_issue_date')} {...hl('id_issue_date')} />
                          </Grid.Col>
                          <Grid.Col span={3}>
                            <TextInput label="Expiry date" type="date" {...form.getInputProps('id_expiry_date')} {...hl('id_expiry_date')} />
                          </Grid.Col>
                        </>
                      )}
                      {/* Beta #14 (2026-05-26): PEP status + US Person (FATCA)
                          moved here from Contact. Both apply to natural and
                          legal kinds.
                          Beta v3 polish A (2026-05-27): US Person became a
                          Yes/No Select for visual parity with PEP, and both
                          fields are sized to span={3} so they sit
                          vertically aligned and side-by-side. */}
                      {/* P4 (2026-10-09, Pedro): English labels, blank by
                          default ("Not answered"), "No" first; clearing maps
                          back to '' so NULLABLE_ON_CLEAR sends null. */}
                      {/* Pedro 2026-10-13: a legal person can be a US person but never a
                          PEP, so a company's Identity tab shows no PEP field. */}
                      {!isLegal && (
                        <Grid.Col span={3}>
                          {/* Pedro 2026-10-13: the PEP references, one click from the field. */}
                          <Select
                            label={<LabelWithLinks text="PEP status" title="Politically exposed persons" links={PEP_LINKS} />}
                            data={PEP_OPTIONS}
                            placeholder={NOT_ANSWERED}
                            {...form.getInputProps('pep_status')} {...hl('pep_status')}
                            onChange={(v) => form.setFieldValue('pep_status', v ?? '')}
                            clearable />
                        </Grid.Col>
                      )}
                      <Grid.Col span={3}>
                        {/* Pedro 2026-10-13: the IRS definition, one click from the field (a
                            link, so it works in Read mode too); opens in a new tab. */}
                        <Select
                          label={<LabelWithLinks text="US person (FATCA)" title="Who is a US person?" links={US_PERSON_LINKS} />}
                          data={US_PERSON_OPTIONS}
                          placeholder={NOT_ANSWERED}
                          {...form.getInputProps('us_person')} {...hl('us_person')}
                          onChange={(v) => form.setFieldValue('us_person', (v ?? '') as FormValues['us_person'])}
                          clearable />
                      </Grid.Col>
                    </Grid>
                  </Fieldset>
                  </fieldset>
                </Tabs.Panel>

                <Tabs.Panel value="kyc" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <Fieldset legend="Per-Person KYC review">
                    <Text size="xs" c="dimmed" mb="sm">
                      Tracks when this individual fiche was last refreshed and when the next refresh is due.
                      Distinct from the Agreement-level KYC review, which marks when the whole agreement
                      file (parties, accounts, documents) was last audited end-to-end.
                    </Text>
                    <Grid>
                      <Grid.Col span={4}>
                        <TextInput label="Last KYC date" type="date"
                          {...form.getInputProps('kyc_last_date')} {...hl('kyc_last_date')} />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <TextInput label="Next KYC date" type="date"
                          {...form.getInputProps('kyc_next_date')} {...hl('kyc_next_date')} />
                      </Grid.Col>
                    </Grid>
                  </Fieldset>
                  {/* P16b (Pedro 2026-10-10): the client record's financial blocks, held
                      per person. Source of wealth, expected flows and the tax declaration
                      are required of a holder (completeness); the rest is optional. */}
                  {!isLegal && (
                    <Stack mt="md">
                      {/* Smoke 2026-10-11 (S3, Pedro): every amount below is in thousands of euros. */}
                      <Text size="sm" fw={600} c="dimmed">Amounts in &apos;000 Euros</Text>
                      <Select label="Tax obligations met (declared by the client)" data={YES_NO_OPTS}
                        placeholder={NOT_ANSWERED} clearable w={300}
                        {...form.getInputProps('tax_obligations_met')} {...hl('tax_obligations_met')}
                        onChange={(v) => form.setFieldValue('tax_obligations_met', (v ?? '') as FormValues['tax_obligations_met'])} />
                      <SourceOfWealthEditor value={form.values.source_of_wealth}
                        onChange={(v) => form.setFieldValue('source_of_wealth', v)}
                        hl={missing.block('source_of_wealth', form.values.source_of_wealth)} />
                      <ExpectedFlowsEditor value={form.values.expected_flows}
                        onChange={(v) => form.setFieldValue('expected_flows', v)}
                        hl={missing.block('expected_flows', form.values.expected_flows)} />
                      <FinancialSituationEditor value={form.values.financial_situation}
                        onChange={(v) => form.setFieldValue('financial_situation', v)}
                        hl={missing.block('financial_situation', form.values.financial_situation)} />
                    </Stack>
                  )}
                  </fieldset>
                </Tabs.Panel>

                <Tabs.Panel value="contact" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <Fieldset>
                    {/* Beta #14 (2026-05-26): PEP + US Person have moved
                        to the Identity tab; Contact is now strictly
                        reachability (email + phones). */}
                    <Grid>
                      <Grid.Col span={6}><TextInput label="Email" type="email" {...form.getInputProps('email')} {...hl('email')} /></Grid.Col>
                      <Grid.Col span={2}>
                        <NumberInput label="Mobile country code" min={1} max={9999} hideControls
                          value={form.values.mobile_country_code === '' ? '' : Number(form.values.mobile_country_code)}
                          onChange={(v) => form.setFieldValue('mobile_country_code', v === '' ? '' : String(v))} />
                      </Grid.Col>
                      <Grid.Col span={4}><TextInput label="Mobile number" {...form.getInputProps('mobile_num')} {...hl('mobile_num')} /></Grid.Col>
                      <Grid.Col span={2}>
                        <NumberInput label="Fixed-line country code" min={1} max={9999} hideControls
                          value={form.values.fixed_phone_country_code === '' ? '' : Number(form.values.fixed_phone_country_code)}
                          onChange={(v) => form.setFieldValue('fixed_phone_country_code', v === '' ? '' : String(v))} />
                      </Grid.Col>
                      <Grid.Col span={4}><TextInput label="Fixed-line number" {...form.getInputProps('fixed_phone_num')} /></Grid.Col>
                    </Grid>
                  </Fieldset>
                  {/* P13 (Pedro 2026-10-10): which emails this person receives. Unticked
                      = not receiving (consent is explicit). Saved with Save; an RM's change
                      is filed for approval. The Persons list has the same checkboxes. */}
                  <Fieldset legend="Email preferences" mt="md">
                    <Group gap="xl">
                      {COMMS_TYPES.map(({ field, label }) => (
                        <Checkbox key={field} label={label}
                          {...form.getInputProps(field, { type: 'checkbox' })} />
                      ))}
                    </Group>
                  </Fieldset>
                  </fieldset>
                </Tabs.Panel>

                {!isLegal && (
                  <Tabs.Panel value="birth" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <Fieldset>
                      <Grid>
                        <Grid.Col span={3}><Select label="Gender" data={GENDER_OPTS} {...form.getInputProps('gender')} {...hl('gender')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Birth date" type="date" {...form.getInputProps('birth_date')} {...hl('birth_date')} /></Grid.Col>
                        <Grid.Col span={5}><TextInput label="Nationality (primary)" {...form.getInputProps('nationality_1')} {...hl('nationality_1')} /></Grid.Col>
                        <Grid.Col span={5}><TextInput label="Nationality (secondary)" {...form.getInputProps('nationality_2')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="Birth country" {...form.getInputProps('birth_place_country')} {...hl('birth_place_country')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Birth state / region" {...form.getInputProps('birth_place_state')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Birth district" {...form.getInputProps('birth_place_district')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Birth city" {...form.getInputProps('birth_place_city')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Birth freguesia (parish)" {...form.getInputProps('birth_place_freguesia')} /></Grid.Col>
                      </Grid>
                    </Fieldset>
                    </fieldset>
                  </Tabs.Panel>
                )}

                {/* Beta #13 (2026-05-26): Identification tab removed.
                    ID fields now live inside the Identity tab above
                    (gated on !isLegal — Legal entities don't have IDs). */}

                {!isLegal && (
                  <Tabs.Panel value="fiscal" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <Fieldset>
                    <Grid>
                      <Grid.Col span={6}><TextInput label="Fiscal country (primary)" {...form.getInputProps('fiscal_country_1')} {...hl('fiscal_country_1')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="TIN / NIF (primary)" {...form.getInputProps('tin_1')} {...hl('tin_1')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Fiscal country (2nd)" {...form.getInputProps('fiscal_country_2')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="TIN / NIF (2nd)" {...form.getInputProps('tin_2')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Fiscal country (3rd)" {...form.getInputProps('fiscal_country_3')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="TIN / NIF (3rd)" {...form.getInputProps('tin_3')} /></Grid.Col>
                    </Grid>
                  </Fieldset>
                  </fieldset>
                </Tabs.Panel>
                )}

                {!isLegal && (
                  <Tabs.Panel value="profession" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <Fieldset>
                      <Grid>
                        <Grid.Col span={4}><Select label="Professional status" data={PROF_STATUS_OPTS} {...form.getInputProps('professional_status')} {...hl('professional_status')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Profession" {...form.getInputProps('profession')} {...hl('profession')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Employer" {...form.getInputProps('employer')} {...hl('employer')} /></Grid.Col>
                      </Grid>
                    </Fieldset>
                    </fieldset>
                  </Tabs.Panel>
                )}

                <Tabs.Panel value="addresses" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <Stack>
                    {/* #68 (2026-06-28): primary address is kind-aware. A LEGAL entity's
                        address is its registered office (fiscal_address* is CHECK-forbidden
                        for LEGAL — migration 0001 persons_natural_fields_only_when_natural). */}
                    {isLegal ? (
                    <Fieldset legend="Registered office">
                      <Grid>
                        <Grid.Col span={12}><TextInput label="Street" {...form.getInputProps('registered_office_address')} /></Grid.Col>
                        <Grid.Col span={2}><TextInput label="Postal code" {...form.getInputProps('registered_office_postal_code')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="City" {...form.getInputProps('registered_office_city')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="State / region" {...form.getInputProps('registered_office_state')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="Country" {...form.getInputProps('registered_office_country')} /></Grid.Col>
                      </Grid>
                    </Fieldset>
                    ) : (
                    <Fieldset legend="Fiscal address">
                      <Grid>
                        <Grid.Col span={12}><TextInput label="Street" {...form.getInputProps('fiscal_address')} /></Grid.Col>
                        <Grid.Col span={2}><TextInput label="Postal code" {...form.getInputProps('fiscal_address_postal_code')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="City" {...form.getInputProps('fiscal_address_city')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="State / region" {...form.getInputProps('fiscal_address_state')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="Country" {...form.getInputProps('fiscal_address_country')} /></Grid.Col>
                      </Grid>
                    </Fieldset>
                    )}
                    <Fieldset legend="Correspondence (mail) address">
                      <Stack>
                        <Group gap="sm" align="center">
                          <Button size="compact-xs" variant="subtle" leftSection={<IconCopy size={12} />}
                            onClick={copyFiscalToMail}>
                            Same as fiscal
                          </Button>
                        </Group>
                        <Grid>
                          <Grid.Col span={12}><TextInput label="Street" {...form.getInputProps('mail_address')} /></Grid.Col>
                          <Grid.Col span={2}><TextInput label="Postal code" {...form.getInputProps('mail_address_postal_code')} /></Grid.Col>
                          <Grid.Col span={4}><TextInput label="City" {...form.getInputProps('mail_address_city')} /></Grid.Col>
                          <Grid.Col span={3}><TextInput label="State / region" {...form.getInputProps('mail_address_state')} /></Grid.Col>
                          <Grid.Col span={3}><TextInput label="Country" {...form.getInputProps('mail_address_country')} /></Grid.Col>
                        </Grid>
                      </Stack>
                    </Fieldset>
                  </Stack>
                  </fieldset>
                </Tabs.Panel>

                {!isLegal && (
                  <Tabs.Panel value="marital" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <Fieldset legend="Spouse">
                      <Grid>
                        <Grid.Col span={6}><Select label="Marital status" data={MARITAL_STATUS_OPTS} {...form.getInputProps('marital_status')} {...hl('marital_status')} /></Grid.Col>
                        <Grid.Col span={6}><Select label="Marital regime" data={MARITAL_REGIME_OPTS} {...form.getInputProps('marital_regime')} /></Grid.Col>
                        <Grid.Col span={12}><TextInput label="Spouse - full name" {...form.getInputProps('spouse_name')} /></Grid.Col>
                        <Grid.Col span={3}><Select label="Spouse - ID type" data={ID_TYPE_OPTS} {...form.getInputProps('spouse_id_type')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="Spouse - ID number" {...form.getInputProps('spouse_id_number')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="Spouse - ID expiry" type="date" {...form.getInputProps('spouse_id_expiry_date')} /></Grid.Col>
                        <Grid.Col span={6}><TextInput label="Spouse - fiscal country" {...form.getInputProps('spouse_fiscal_country')} /></Grid.Col>
                        <Grid.Col span={6}><TextInput label="Spouse - TIN / NIF" {...form.getInputProps('spouse_tin')} /></Grid.Col>
                      </Grid>
                    </Fieldset>
                    {/* P16b: the number of children first, then that many lines. */}
                    <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
                      <ChildrenBox
                        count={form.values.children_count === '' ? null : Number(form.values.children_count)}
                        rows={form.values.children}
                        onChange={(count, rows) => {
                          form.setFieldValue('children_count', count === null ? '' : String(count));
                          form.setFieldValue('children', rows);
                        }}
                      />
                    </div>
                    </fieldset>
                  </Tabs.Panel>
                )}

                {isLegal && (
                  <Tabs.Panel value="legal" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <Fieldset>
                      <Grid>
                        <Grid.Col span={6}><TextInput label="Legal form" {...form.getInputProps('legal_form')} {...hl('legal_form')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="LEI (Legal Entity Identifier)" maxLength={20} {...form.getInputProps('lei')} {...hl('lei')} /></Grid.Col>
                        {/* Beta #5 (2026-05-26): LEI codes are valid 12 months;
                            Compliance surfaces this date on the expiring dashboard. */}
                        <Grid.Col span={3}><TextInput label="LEI expiry" type="date"
                          {...form.getInputProps('lei_expiry_date')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="NIPC" maxLength={9} {...form.getInputProps('nipc')} {...hl('nipc')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="Incorporation country" {...form.getInputProps('incorporation_country')} {...hl('incorporation_country')} /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="Incorporation date" type="date" {...form.getInputProps('incorporation_date')} {...hl('incorporation_date')} /></Grid.Col>
                      </Grid>
                      {/* Inc 0 (migration 0057): general, editable TIN for legal
                          entities (the billing-identity resolver reads this for
                          legal parties). Own row so TIN sits under NIPC and Fiscal
                          country under Incorporation country; align="end" bottom-
                          aligns the boxes despite TIN's description and lines the
                          button up with them. "Copy from NIPC + country" prefills
                          TIN from NIPC and Fiscal country from Incorporation country
                          (PT entities: these usually match); both stay editable. */}
                      <Grid align="end">
                        <Grid.Col span={3}><TextInput label="TIN / Tax ID" {...form.getInputProps('tin')} {...hl('tin')}
                          description="General tax identifier (PT: equals NIPC)" /></Grid.Col>
                        <Grid.Col span={3}><TextInput label="Fiscal country" {...form.getInputProps('fiscal_country')} /></Grid.Col>
                        <Grid.Col span={3}><Button variant="light"
                          disabled={!form.values.nipc && !form.values.incorporation_country}
                          onClick={() => {
                            form.setFieldValue('tin', form.values.nipc);
                            form.setFieldValue('fiscal_country', form.values.incorporation_country);
                          }}>Copy from NIPC + country</Button></Grid.Col>
                      </Grid>
                      <Grid>
                        {/* #68 (2026-06-28): registered-office address moved to the Addresses
                            tab; the Legal tab keeps legal-identity fields only. */}
                        <Grid.Col span={12}><TextInput label="Regulatory authority" {...form.getInputProps('regulatory_authority')} {...hl('regulatory_authority')}
                          description="If supervised (e.g. CMVM, BdP)" /></Grid.Col>
                      </Grid>
                    </Fieldset>
                    {/* P16b (Pedro 2026-10-10): the client record's company fields. Required
                        of a holder company (completeness). */}
                    <Fieldset legend="Company profile" mt="md">
                      <Grid align="end">
                        <Grid.Col span={8}>
                          <Textarea label="Corporate purpose" autosize minRows={2}
                            {...form.getInputProps('corporate_purpose')} {...hl('corporate_purpose')} />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <TextInput label="CAE code" description="Economic activity code"
                            {...form.getInputProps('cae_code')} {...hl('cae_code')} />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <Select label="Listed company" data={YES_NO_OPTS} placeholder={NOT_ANSWERED} clearable
                            {...form.getInputProps('listed_company')} {...hl('listed_company')}
                            onChange={(v) => {
                              form.setFieldValue('listed_company', (v ?? '') as FormValues['listed_company']);
                              // The market belongs to a listed company only.
                              if (v !== 'Yes') form.setFieldValue('listed_market', '');
                            }} />
                        </Grid.Col>
                        <Grid.Col span={8}>
                          {/* Smoke 2026-10-11 (S5): no example once the company is NOT listed. */}
                          <TextInput label="Market"
                            placeholder={form.values.listed_company === 'Yes' ? 'e.g. Euronext Lisbon'
                              : form.values.listed_company === 'No' ? 'n.a.' : '—'}
                            disabled={form.values.listed_company !== 'Yes'}
                            {...form.getInputProps('listed_market')} />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Textarea label="Operations in high-risk countries (declaration)" autosize minRows={2}
                            description="The company's declaration of any operations in high-risk countries ('Conexão' on the client record)."
                            {...form.getInputProps('high_risk_countries_declaration')} {...hl('high_risk_countries_declaration')} />
                        </Grid.Col>
                        <Grid.Col span={8}>
                          <Textarea label="Ownership structure" autosize minRows={3}
                            description="The ownership chain up to the ultimate beneficial owners."
                            {...form.getInputProps('ownership_structure')} {...hl('ownership_structure')} />
                        </Grid.Col>
                        <Grid.Col span={4} style={{ alignSelf: 'flex-start' }}>
                          {person && <UboList personId={person.id} />}
                        </Grid.Col>
                      </Grid>
                    </Fieldset>
                    {/* Beta #6b (2026-05-26): PCC + RCBE registry documents.
                        Compliance gets expiring-date alerts as either approaches expiry. */}
                    <Fieldset legend="Registry documents" mt="md">
                      {/* Beta v2 polish D (2026-05-26): paired inputs where
                          one has a description and the next doesn't would
                          misalign at the bottom. align="end" pushes all
                          row items to the bottom. Apply this pattern any
                          time a Grid row mixes inputs with/without descriptions. */}
                      <Grid align="end">
                        <Grid.Col span={8}><TextInput label="PCC code"
                          description="Certidão Permanente Comercial — access code"
                          {...form.getInputProps('pcc_code')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="PCC expiry" type="date"
                          {...form.getInputProps('pcc_expiry_date')} /></Grid.Col>
                        <Grid.Col span={8}><TextInput label="RCBE code"
                          description="Registo Central do Beneficiário Efectivo"
                          {...form.getInputProps('rcbe_code')} /></Grid.Col>
                        <Grid.Col span={4}><TextInput label="RCBE expiry" type="date"
                          {...form.getInputProps('rcbe_expiry_date')} /></Grid.Col>
                      </Grid>
                    </Fieldset>
                    </fieldset>
                  </Tabs.Panel>
                )}

                {!isNew && person && (
                  <Tabs.Panel value="roles" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <RolesSection
                      personId={person.id}
                      personLabel={person.display_name}
                      editMode={editMode}
                      agrOps={agrPartyQ.ops}
                      agrFailedKeys={agrPartyQ.failedKeys}
                      onStageAgrCreate={stageAgrRoleCreate}
                      onStageAgrDelete={stageAgrRoleDelete}
                      onUnstageAgr={agrPartyQ.unstage}
                      acctOps={acctPartyQ.ops}
                      acctFailedKeys={acctPartyQ.failedKeys}
                      onStageAcctCreate={stageAcctRoleCreate}
                      onStageAcctDelete={stageAcctRoleDelete}
                      onUnstageAcct={acctPartyQ.unstage}
                      polOps={polPartyQ.ops}
                      polFailedKeys={polPartyQ.failedKeys}
                      onStagePolCreate={stagePolRoleCreate}
                      onStagePolDelete={stagePolRoleDelete}
                      onUnstagePol={polPartyQ.unstage}
                    />
                    </fieldset>
                  </Tabs.Panel>
                )}

                {/* P23 (2026-10-10): files on this person. Not inside a disabled
                    fieldset — View / Download work in Read mode; the panel gates its
                    own write affordances on Edit mode. */}
                {!isNew && person && (
                  <Tabs.Panel value="documents" pt="md">
                    <DocumentsPanel
                      ownerType="persons"
                      ownerId={person.id}
                      personKind={person.kind}
                      editMode={editMode}
                      staging={docs}
                      noAddKinds={['PHOTO']}
                    />
                  </Tabs.Panel>
                )}
              </Tabs>

            {!isNew && person && (
              <Text size="xs" c="dimmed" ta="right" mt="md">
                Last updated {new Date(person.updated_at).toLocaleString()}
              </Text>
            )}
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}

const ROLE_COLOURS: Record<string, string> = {
  // agreement_parties + account_parties
  HOLDER: 'blue',
  REPRESENTATIVE: 'grape',
  BENEFICIARY: 'orange',
  JOINT_HOLDER: 'teal',
  INSURANCE_BENEFICIARY: 'orange',  // legacy, retained for any pre-H1.5 row
  // insurance_policy_parties (Phase H1.5)
  POLICY_HOLDER: 'blue',
  LIFE_INSURED: 'teal',
  // BENEFICIARY already mapped above
};

function RolesSection({
  personId, personLabel, editMode,
  agrOps, agrFailedKeys, onStageAgrCreate, onStageAgrDelete, onUnstageAgr,
  acctOps, acctFailedKeys, onStageAcctCreate, onStageAcctDelete, onUnstageAcct,
  polOps, polFailedKeys, onStagePolCreate, onStagePolDelete, onUnstagePol,
}: {
  personId: string;
  personLabel: string;
  editMode: boolean;
  agrOps: readonly PendingAgrPartyOp[];
  agrFailedKeys: ReadonlySet<string>;
  onStageAgrCreate: (payload: AgreementPartyCreateInput) => void;
  onStageAgrDelete: (party_id: string) => void;
  onUnstageAgr: (key: string) => void;
  acctOps: readonly PendingAcctPartyOp[];
  acctFailedKeys: ReadonlySet<string>;
  onStageAcctCreate: (payload: AccountPartyCreateInput) => void;
  onStageAcctDelete: (party_id: string) => void;
  onUnstageAcct: (key: string) => void;
  polOps: readonly PendingPolPartyOp[];
  polFailedKeys: ReadonlySet<string>;
  onStagePolCreate: (payload: InsurancePolicyPartyInput) => void;
  onStagePolDelete: (party_id: string) => void;
  onUnstagePol: (key: string) => void;
}) {
  const [roleModalOpen, setRoleModalOpen] = React.useState(false);
  const [acctRoleModalOpen, setAcctRoleModalOpen] = React.useState(false);
  const [polRoleModalOpen, setPolRoleModalOpen] = React.useState(false);
  const agreementParties = useAgreementParties({ person_id: personId, limit: 200 });
  const accountParties = useAccountParties({ person_id: personId, limit: 200 });
  const insurance = useAccountInsuranceList({ person_id: personId, limit: 200 });
  // Phase H1.6: direct policy-level roles (POLICY_HOLDER / LIFE_INSURED /
  // BENEFICIARY) via insurance_policy_parties. Distinct from "Insurance
  // policies attached" (the table below) which lists policies the person
  // is INDIRECTLY connected to via agreement-level or account-level roles.
  const policyParties = useInsurancePolicyPartiesList(undefined, undefined, personId, { limit: 200 });

  const agreementRows = agreementParties.data?.items ?? [];
  const accountRows = accountParties.data?.items ?? [];
  const insuranceRows = insurance.data?.items ?? [];
  const policyPartyRows = policyParties.data?.items ?? [];
  const isLoading = agreementParties.isLoading || accountParties.isLoading
    || insurance.isLoading || policyParties.isLoading;

  // Phase 4b-2 (2026-06-16, #11): staged agreement-party create/delete. The
  // old navigate-away "+ New role" <a> (which escaped the disabled fieldset,
  // the C-172 Read-mode leak) is replaced by the modal-that-stages +
  // per-section Add button below.
  const pendingAgrDeleteIds = React.useMemo(
    () => new Set(agrOps.filter((op) => op.kind === 'delete').map((op) => op.party_id)),
    [agrOps],
  );
  const pendingAgrCreates = React.useMemo(
    () => agrOps.filter(
      (op): op is Extract<PendingAgrPartyOp, { kind: 'create' }> => op.kind === 'create',
    ),
    [agrOps],
  );
  // §3j app-wide visibility rule: a FILED (RM-routed) create has no row yet,
  // so render it as a blue phantom row sourced from the pending CR — so RM
  // and any other user editing this person see the in-flight change and
  // don't file a conflicting one. Keyed on person_id (the CR payload carries
  // both agreement_id and person_id). Mirrors AgreementDetailPage's
  // PartiesSection filed-create rows.
  const filedAgrCreates = usePendingCreates('agreement_parties', 'person_id', personId);
  const hasAgrRows = agreementRows.length > 0 || pendingAgrCreates.length > 0
    || filedAgrCreates.length > 0;

  // Commit 5 — account-party staging.
  const pendingAcctDeleteIds = React.useMemo(
    () => new Set(acctOps.filter((op) => op.kind === 'delete').map((op) => op.party_id)),
    [acctOps],
  );
  const pendingAcctCreates = React.useMemo(
    () => acctOps.filter(
      (op): op is Extract<PendingAcctPartyOp, { kind: 'create' }> => op.kind === 'create',
    ),
    [acctOps],
  );
  const filedAcctCreates = usePendingCreates('account_parties', 'person_id', personId);
  const hasAcctRows = accountRows.length > 0 || pendingAcctCreates.length > 0
    || filedAcctCreates.length > 0;

  // Commit 6 — policy-party staging.
  const pendingPolDeleteIds = React.useMemo(
    () => new Set(polOps.filter((op) => op.kind === 'delete').map((op) => op.party_id)),
    [polOps],
  );
  const pendingPolCreates = React.useMemo(
    () => polOps.filter(
      (op): op is Extract<PendingPolPartyOp, { kind: 'create' }> => op.kind === 'create',
    ),
    [polOps],
  );
  const filedPolCreates = usePendingCreates('insurance_policy_parties', 'person_id', personId);
  const hasPolRows = policyPartyRows.length > 0 || pendingPolCreates.length > 0
    || filedPolCreates.length > 0;

  return (
    <Stack>
      <AddPersonAgreementRoleModal
        opened={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        personId={personId}
        personLabel={personLabel}
        onStage={onStageAgrCreate}
      />
      <AddPersonAccountRoleModal
        opened={acctRoleModalOpen}
        onClose={() => setAcctRoleModalOpen(false)}
        personId={personId}
        personLabel={personLabel}
        onStage={onStageAcctCreate}
      />
      <AddPersonPolicyRoleModal
        opened={polRoleModalOpen}
        onClose={() => setPolRoleModalOpen(false)}
        personId={personId}
        personLabel={personLabel}
        onStage={onStagePolCreate}
      />
      {isLoading && <Text c="dimmed" size="sm">Loading...</Text>}

      {/* Roles on agreements — staged create/delete (§3g Pattern A, A1).
          Always rendered so "+ Add role" is discoverable even with zero
          rows; greyed-with-tooltip in Read mode (§3h / C-172). Editing an
          existing role still happens on its own detail page via the row
          link (kept). Account + policy roles below stay read-only until
          commits 5/6 (C-164). */}
      <div>
        <Group justify="space-between" mb={6}>
          <Text component="div" size="xs" c="dimmed" tt="uppercase">
            <Group gap={4} display="inline-flex"><IconUsers size={12} /><span>Roles on agreements</span></Group>
          </Text>
          <DisabledTooltipButton
            size="xs" leftSection={<IconPlus size={14} />} variant="light"
            disabledReason={editMode ? null : 'Enter Edit mode to add a role'}
            onClick={() => setRoleModalOpen(true)}>
            Add role
          </DisabledTooltipButton>
        </Group>
        {!isLoading && !hasAgrRows && (
          <Text c="dimmed" size="sm">
            No agreement roles yet. Use "Add role" to attach this person to an
            agreement as a holder, representative or beneficiary.
          </Text>
        )}
        {hasAgrRows && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={140}>Role</Table.Th>
                <Table.Th>Agreement</Table.Th>
                <Table.Th w={80}>UBO</Table.Th>
                <Table.Th w={210}>Status</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {agreementRows.map((p) => {
                const pendingDelete = pendingAgrDeleteIds.has(p.id);
                const failed = agrFailedKeys.has(p.id);
                return (
                  <Table.Tr key={p.id}
                    style={pendingDelete ? { opacity: 0.6 } : undefined}>
                    {/* line-through only on the identifying cells (Role +
                        Agreement) — the status badges, incl. "Pending ·
                        Remove", stay crisp and readable (Pedro 2026-06-16). */}
                    <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}>
                      {/* Row link kept: editing a role stays on its own
                          detail page (Phase 4b-2 is create + delete only). */}
                      <Tooltip label="Click to view / edit this role assignment" withArrow position="top">
                        <Anchor component={Link} to={`/agreement-parties/${p.id}`} size="sm" style={{ cursor: 'pointer' }}>
                          <Badge variant="light" color={ROLE_COLOURS[p.role]}
                            styles={{ root: { cursor: 'pointer' }, label: pendingDelete ? { textDecoration: 'line-through' } : undefined }}>
                            {PARTY_ROLE_LABELS[p.role] ?? p.role}
                          </Badge>
                        </Anchor>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}><AgreementRef id={p.agreement_id} linkPathPrefix="/agreements" /></Table.Td>
                    <Table.Td>{p.is_ubo ? <Badge color="red" variant="light">UBO</Badge> : '-'}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                        {p.deleted_at
                          ? <Badge color="gray" variant="light">Deleted</Badge>
                          : <Badge color="green" variant="light">Active</Badge>}
                        </StatusExplained>
                        {pendingDelete && <Badge color="orange" variant="light" size="xs">Pending · Remove</Badge>}
                        {failed && <Badge color="red" variant="light" size="xs">Failed</Badge>}
                        <PendingCRBadge crs={p.pending_change_requests} />
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <RowSeverIcon
                        pending={pendingDelete}
                        editMode={editMode}
                        deleted={!!p.deleted_at}
                        onStage={() => onStageAgrDelete(p.id)}
                        onUnstage={() => onUnstageAgr(p.id)}
                        stageLabel="Detach this role (on Save)"
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {/* Staged Add-role rows — orange Pending Save treatment,
                  mirrors AgreementDetailPage's PartiesSection. */}
              {pendingAgrCreates.map((op) => {
                const failed = agrFailedKeys.has(op.tempKey);
                return (
                  <Table.Tr key={op.tempKey}
                    style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                    <Table.Td>
                      <Badge variant="light" color={ROLE_COLOURS[op.payload.role]}>
                        {PARTY_ROLE_LABELS[op.payload.role] ?? op.payload.role}
                      </Badge>
                    </Table.Td>
                    <Table.Td><AgreementRef id={op.payload.agreement_id} linkPathPrefix="/agreements" /></Table.Td>
                    <Table.Td>{op.payload.is_ubo ? <Badge color="red" variant="light">UBO</Badge> : '-'}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <Badge color="orange" variant="light" size="xs">Pending · Add role</Badge>
                        {failed && (
                          <Tooltip label="This staged role failed on the last Save — fix the cause (e.g. duplicate role) or remove it, then Save again." withArrow>
                            <Badge color="red" variant="light" size="xs">Failed</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {editMode && (
                        <Tooltip label="Remove this staged role" withArrow>
                          <ActionIcon size="sm" variant="subtle" color="gray"
                            onClick={() => onUnstageAgr(op.tempKey)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {/* §3j: FILED creates (RM-routed) render as blue phantom rows
                  until Compliance decides — visible to RM and to any other
                  user, so a conflicting CR isn't filed against the same row.
                  Blue = filed/awaiting; orange (above) = staged/not-yet-saved. */}
              {filedAgrCreates.map((cr) => {
                const cp = cr.payload as { agreement_id?: string; role?: string; is_ubo?: boolean };
                return (
                  <Table.Tr key={cr.id}
                    style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                    <Table.Td>
                      {cp.role ? (
                        <Badge variant="light" color={ROLE_COLOURS[cp.role] ?? 'gray'}>
                          {PARTY_ROLE_LABELS[cp.role] ?? cp.role}
                        </Badge>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>
                      {cp.agreement_id
                        ? <AgreementRef id={cp.agreement_id} linkPathPrefix="/agreements" />
                        : '—'}
                    </Table.Td>
                    <Table.Td>{cp.is_ubo ? <Badge color="red" variant="light">UBO</Badge> : '-'}</Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                        <Badge color="blue" variant="light" size="xs">Pending approval</Badge>
                      </Anchor>
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </div>

      {/* Roles on accounts — staged create/delete (§3g Pattern A, commit 5). */}
      <div>
        <Group justify="space-between" mb={6} mt="md">
          <Text component="div" size="xs" c="dimmed" tt="uppercase">
            <Group gap={4} display="inline-flex"><IconBuildingBank size={12} /><span>Roles on accounts</span></Group>
          </Text>
          <DisabledTooltipButton
            size="xs" leftSection={<IconPlus size={14} />} variant="light"
            disabledReason={editMode ? null : 'Enter Edit mode to add a role'}
            onClick={() => setAcctRoleModalOpen(true)}>
            Add role
          </DisabledTooltipButton>
        </Group>
        {!isLoading && !hasAcctRows && (
          <Text c="dimmed" size="sm">
            No account roles yet. Use "Add role" to attach this person to an account.
          </Text>
        )}
        {hasAcctRows && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={140}>Role</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th w={210}>Status</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {accountRows.map((p) => {
                const pendingDelete = pendingAcctDeleteIds.has(p.id);
                const failed = acctFailedKeys.has(p.id);
                return (
                  <Table.Tr key={p.id} style={pendingDelete ? { opacity: 0.6 } : undefined}>
                    <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}>
                      <Anchor component={Link} to={`/account-parties/${p.id}`} size="sm" style={{ cursor: 'pointer' }}>
                        <Badge variant="light" color={ROLE_COLOURS[p.role]}
                          styles={{ root: { cursor: 'pointer' }, label: pendingDelete ? { textDecoration: 'line-through' } : undefined }}>
                          {PARTY_ROLE_LABELS[p.role] ?? p.role}
                        </Badge>
                      </Anchor>
                    </Table.Td>
                    <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}><AccountRef id={p.account_id} linkPathPrefix="/accounts" /></Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                        {p.deleted_at
                          ? <Badge color="gray" variant="light">Deleted</Badge>
                          : <Badge color="green" variant="light">Active</Badge>}
                        </StatusExplained>
                        {pendingDelete && <Badge color="orange" variant="light" size="xs">Pending · Remove</Badge>}
                        {failed && <Badge color="red" variant="light" size="xs">Failed</Badge>}
                        <PendingCRBadge crs={p.pending_change_requests} />
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <RowSeverIcon
                        pending={pendingDelete}
                        editMode={editMode}
                        deleted={!!p.deleted_at}
                        onStage={() => onStageAcctDelete(p.id)}
                        onUnstage={() => onUnstageAcct(p.id)}
                        stageLabel="Detach this role (on Save)"
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {pendingAcctCreates.map((op) => {
                const failed = acctFailedKeys.has(op.tempKey);
                return (
                  <Table.Tr key={op.tempKey} style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                    <Table.Td>
                      <Badge variant="light" color={ROLE_COLOURS[op.payload.role]}>
                        {PARTY_ROLE_LABELS[op.payload.role] ?? op.payload.role}
                      </Badge>
                    </Table.Td>
                    <Table.Td><AccountRef id={op.payload.account_id} linkPathPrefix="/accounts" /></Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <Badge color="orange" variant="light" size="xs">Pending · Add role</Badge>
                        {failed && (
                          <Tooltip label="This staged role failed on the last Save — fix the cause (e.g. duplicate role) or remove it, then Save again." withArrow>
                            <Badge color="red" variant="light" size="xs">Failed</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {editMode && (
                        <Tooltip label="Remove this staged role" withArrow>
                          <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => onUnstageAcct(op.tempKey)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {filedAcctCreates.map((cr) => {
                const cp = cr.payload as { account_id?: string; role?: string };
                return (
                  <Table.Tr key={cr.id} style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                    <Table.Td>
                      {cp.role ? (
                        <Badge variant="light" color={ROLE_COLOURS[cp.role] ?? 'gray'}>
                          {PARTY_ROLE_LABELS[cp.role] ?? cp.role}
                        </Badge>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>{cp.account_id ? <AccountRef id={cp.account_id} linkPathPrefix="/accounts" /> : '—'}</Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                        <Badge color="blue" variant="light" size="xs">Pending approval</Badge>
                      </Anchor>
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </div>

      {/* Direct roles on policies — staged create/delete (§3g Pattern A, commit 6). */}
      <div>
        <Group justify="space-between" mb={6} mt="md">
          <Text component="div" size="xs" c="dimmed" tt="uppercase">
            <Group gap={4} display="inline-flex"><IconShieldCheck size={12} /><span>Direct roles on policies</span></Group>
          </Text>
          <DisabledTooltipButton
            size="xs" leftSection={<IconPlus size={14} />} variant="light"
            disabledReason={editMode ? null : 'Enter Edit mode to add a role'}
            onClick={() => setPolRoleModalOpen(true)}>
            Add role
          </DisabledTooltipButton>
        </Group>
        {!isLoading && !hasPolRows && (
          <Text c="dimmed" size="sm">
            No direct policy roles yet. Use "Add role" to attach this person to a policy
            as holder, life insured or beneficiary.
          </Text>
        )}
        {hasPolRows && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={140}>Role</Table.Th>
                <Table.Th>Policy</Table.Th>
                <Table.Th w={120}>Designation</Table.Th>
                <Table.Th w={210}>Status</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {policyPartyRows.map((p) => {
                const pendingDelete = pendingPolDeleteIds.has(p.id);
                const failed = polFailedKeys.has(p.id);
                return (
                  <Table.Tr key={p.id} style={pendingDelete ? { opacity: 0.6 } : undefined}>
                    <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}>
                      <Anchor component={Link}
                        to={`/insurance-policy-parties/${p.id}`} size="sm" style={{ cursor: 'pointer' }}>
                        <Badge variant="light" color={ROLE_COLOURS[p.role] ?? 'gray'}
                          styles={{ root: { cursor: 'pointer' }, label: pendingDelete ? { textDecoration: 'line-through' } : undefined }}>
                          {PARTY_ROLE_LABELS[p.role] ?? p.role}
                        </Badge>
                      </Anchor>
                    </Table.Td>
                    <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}>
                      <Anchor component={Link} to={`/account-insurance/${p.policy_id}`} size="sm">
                        {p.policy_label
                          ? <Text size="xs">{p.policy_label}</Text>
                          : <Text size="xs" ff="monospace">{p.policy_id.slice(0, 8)}…</Text>}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      {p.designation
                        ? <Badge variant="light">{p.designation}</Badge>
                        : <Text c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                        {p.deleted_at
                          ? <Badge color="gray" variant="light">Deleted</Badge>
                          : <Badge color="green" variant="light">Active</Badge>}
                        </StatusExplained>
                        {pendingDelete && <Badge color="orange" variant="light" size="xs">Pending · Remove</Badge>}
                        {failed && <Badge color="red" variant="light" size="xs">Failed</Badge>}
                        <PendingCRBadge crs={p.pending_change_requests} />
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <RowSeverIcon
                        pending={pendingDelete}
                        editMode={editMode}
                        deleted={!!p.deleted_at}
                        onStage={() => onStagePolDelete(p.id)}
                        onUnstage={() => onUnstagePol(p.id)}
                        stageLabel="Detach this role (on Save)"
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {pendingPolCreates.map((op) => {
                const failed = polFailedKeys.has(op.tempKey);
                return (
                  <Table.Tr key={op.tempKey} style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                    <Table.Td>
                      <Badge variant="light" color={op.payload.role ? ROLE_COLOURS[op.payload.role] ?? 'gray' : 'gray'}>
                        {op.payload.role ? PARTY_ROLE_LABELS[op.payload.role] ?? op.payload.role : '—'}
                      </Badge>
                    </Table.Td>
                    <Table.Td><InsurancePolicyRef id={op.payload.policy_id} linkPathPrefix="/account-insurance" /></Table.Td>
                    <Table.Td>
                      {op.payload.designation
                        ? <Badge variant="light">{op.payload.designation}</Badge>
                        : <Text c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <Badge color="orange" variant="light" size="xs">Pending · Add role</Badge>
                        {failed && (
                          <Tooltip label="This staged role failed on the last Save — fix the cause (e.g. duplicate role) or remove it, then Save again." withArrow>
                            <Badge color="red" variant="light" size="xs">Failed</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {editMode && (
                        <Tooltip label="Remove this staged role" withArrow>
                          <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => onUnstagePol(op.tempKey)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {filedPolCreates.map((cr) => {
                const cp = cr.payload as { policy_id?: string; role?: string; designation?: string };
                return (
                  <Table.Tr key={cr.id} style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                    <Table.Td>
                      {cp.role ? (
                        <Badge variant="light" color={ROLE_COLOURS[cp.role] ?? 'gray'}>
                          {PARTY_ROLE_LABELS[cp.role] ?? cp.role}
                        </Badge>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>{cp.policy_id ? <InsurancePolicyRef id={cp.policy_id} linkPathPrefix="/account-insurance" /> : '—'}</Table.Td>
                    <Table.Td>
                      {cp.designation ? <Badge variant="light">{cp.designation}</Badge> : <Text c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                        <Badge color="blue" variant="light" size="xs">Pending approval</Badge>
                      </Anchor>
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </div>

      {insuranceRows.length > 0 && (
        <div>
          {/* Task #259 (2026-06-04): same Text-as-div fix. */}
          <Text component="div" size="xs" c="dimmed" tt="uppercase" mb={6}
            mt={agreementRows.length > 0 || accountRows.length > 0 || policyPartyRows.length > 0 ? 'md' : 0}>
            <Group gap={4} display="inline-flex"><IconShieldCheck size={12} /><span>Insurance policies attached</span></Group>
          </Text>
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Insurer</Table.Th>
                <Table.Th>Policy #</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th>Fund #</Table.Th>
                <Table.Th w={120}>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {insuranceRows.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/account-insurance/${p.id}`} size="sm" fw={500}>
                      {p.insurer}
                    </Anchor>
                  </Table.Td>
                  <Table.Td><Text ff="monospace" size="sm">{p.policy_number}</Text></Table.Td>
                  <Table.Td>
                    {/* H1.6: Policy <-> Account is M:N. Render every linked account. */}
                    {p.account_ids.length === 0
                      ? <Text c="dimmed" size="sm">-</Text>
                      : <Stack gap={2}>
                          {p.account_ids.map((aid) => (
                            <AccountRef key={aid} id={aid} linkPathPrefix="/accounts" />
                          ))}
                        </Stack>}
                  </Table.Td>
                  <Table.Td><Text ff="monospace" size="sm">{p.fund_number ?? '-'}</Text></Table.Td>
                  <Table.Td>
                    {/* P47: the policy's real status (this showed "Active" for every policy
                        not deleted, even an Inactive or Closed one), explained on hover. */}
                    <StatusExplained entity="policy" status={p.deleted_at ? 'Deleted' : p.status}>
                      {p.deleted_at
                        ? <Badge color="gray" variant="light">Deleted</Badge>
                        : <Badge variant="light"
                            color={p.status === 'Active' ? 'green' : p.status === 'Inactive' ? 'yellow' : 'red'}>
                            {p.status}
                          </Badge>}
                    </StatusExplained>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
    </Stack>
  );
}
